// components/MultiStepForm/MultiStepForm.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import debounce from 'lodash.debounce';
import { useParams, useNavigate } from 'react-router-dom';
import { createRoot } from 'react-dom/client';
import axios from 'axios';
import {
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import DOMPurify from 'dompurify';
import '../../styles/MultiStepForm.css';
import { renderToString } from 'react-dom/server';
import ReactDOM from 'react-dom';
// Import company config
import {
  COMPANY_CONFIG,
  isACS,
  isInterbel,
  isArchtop,
  isCWF,
  PAYMENT_AVAILABLE,
} from '../../constants/companyConfig';
import { SELF_SIGNUP_PAYLOAD } from '../../utils/constants';
import { useWorkflow } from '../../contexts/WorkflowContext';
import { useAuth } from '../../contexts/AuthContext';
import { generatePaymentIframeHTML } from './staticComponents/PaymentIframe/paymentIframeUrl';
import ModalBox from './modelBox';
import { ELEMENT_IDS } from '../../constants/ids';
import { useToggleProperty, useTriggerAction } from '../../hooks';
import { ProgressTracker } from '../../components/TestWorkflow';
import Header from '../../components/common/Header/Header';
import { renderToHTMLString } from '../../utils/html';
import SuccessNode from '../CustomNodes/SetupAccount/success';
import TermsConditionNode from '../CustomNodes/ReviewCart/termsConditions';
import ServiceSelection from '../CustomNodes/ServiceSelection/serviceSelection';
import GetNotified from '../CustomNodes/GetNotified/GetNotified';
import RegistrationSuccess from '../CustomNodes/GetNotified/RegistrationSuccess';
import SelfSchedulingNode from '../CustomNodes/SelfScheduling/selfScheduling';
import AddOnsNode from '../CustomNodes/AddOns/addOns';
import ReviewCartNode from '../CustomNodes/ReviewCart/reviewCart';
import OrderReceiptNode from '../CustomNodes/OrderReceipt/orderReceipt';
import CARET_UP_SVG from '../../assets/icons/arrow-upcaret.svg';
import CARET_DOWN_SVG from '../../assets/icons/arrow-down-caret.svg';
import { directApiRequest } from '../../constants/apiRequest';
import { HTTP_METHODS } from '../../constants/appConstants';
import { Check, CirclePlus } from 'lucide-react';
import { MakePaymentApiDetails } from './staticComponents/paymentusApiUtils';
import mockProductResponse from './mockProductResponse.json';
import {
  generateDateTime,
  getCardBrand,
  generateCardExpiryDateTime,
  getPaymentTypeCode,
} from './staticComponents/paymentUtils';

const MultiStepForm = () => {
  const { workflowId, nodeId } = useParams();

  // Debug: Log current company configuration
  console.log('🏢 Current Company Config:', {
    name: COMPANY_CONFIG.name,
    fullName: COMPANY_CONFIG.fullName,
    domain: COMPANY_CONFIG.domain,
    features: COMPANY_CONFIG.features,
    ui: COMPANY_CONFIG.ui,
  });
  // Testing instructions
  if (COMPANY_CONFIG.name === 'Archtop Fiber') {
    // console.log('🧪 ARCHTOP Testing Instructions:');
    // console.log('1. Type an address and select from dropdown');
    // console.log('2. Click Next button (1st time) → Should show "Great News!" and hide form fields');
    // console.log('3. Click Next button (2nd time) → Should proceed to next step');
    // console.log('4. Business/Residential cards should use SVG icons that change color');
  } else {
    // console.log('🧪 PRTC/INTERBEL Testing Instructions:');
    // console.log('1. Type an address and select from dropdown → Should show "Great News!" immediately');
    // console.log('2. Click Next button (1 time) → Should proceed to next step');
    // console.log('3. Business/Residential cards should use PNG icons');
  }
  const [workflow, setWorkflow] = useState(null);
  const [templateHtml, setTemplateHtml] = useState('');
  const [isLoadingTemplate, setIsLoadingTemplate] = useState(false);
  const [isLoadingWorkflow, setIsLoadingWorkflow] = useState(true);
  const [currentStepId, setCurrentStepId] = useState(nodeId);
  const [orderedNodes, setOrderedNodes] = useState([]);
  const [addressAlert, setAddressAlert] = useState('');
  const [isContinueVisible, setIsContinueVisible] = useState(false);
  const [ancestorOrigins, setAncestorOrigins] = useState(
    window?.location?.ancestorOrigins
  );

  const [validationFlag, setValidationFlag] = useState(false);
  const [serviceLoad, setServiceLoad] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [subscriberDetails, setSetSubscriberDetails] = useState([]);
  const [serviceAddress, setServiceAddress] = useState({
    addressCityName: '',
    addressLine1: '',
    addressPostalCode: '',
    addressStateAbbr: '',
    serviceAddressId: '',
  });
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressInputValue, setAddressInputValue] = useState('');
  const [isAddressAvailable, setIsAddressAvailable] = useState(false);
  const [externalReference, setexternalReference] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [isAddressLoading, setIsAddressLoading] = useState(false);
  const [signUpPayload, setSignUpPayload] = useState([]);
  const [doubleClickStatus, setDoubleClickStatus] = useState('waiting'); // 'waiting', 'first-click', 'ready-for-second'
  const [mobileNumber, setMobileNumber] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const navigate = useNavigate();
  const [newPaymentDetails, setNewPaymentDetails] = useState({});
  // For Highline, payment cannot be skipped
  const [skipPayment, setSkipPayment] = useState(false);
  const canSkipPayment = COMPANY_CONFIG.name !== 'Highline';
  const [reloadKey, setReloadKey] = useState(0);
  const [autoPayValue, setAutoPayValue] = useState('yes');
  const [paperlessValue, setPaperlessValue] = useState('yes');

  const [allSubscriberDetails, setallSubscriberDetails] = useState([]);

  const { token, setToken } = useAuth();
  const [accessType, setAccessType] = useState('admin');
  const [subscriberType, setSubscriberType] = useState('R');
  const [accountType, setAccountType] = useState('Residential');
  const [expandedProduct, setExpandedProduct] = useState({});

  const getAccessTokenFromUrl = () => {
    const queryParams = new URLSearchParams(window.location.search);
    return queryParams.get('accessToken');
  };

  useEffect(() => {
    if (!token) {
      const urlToken = getAccessTokenFromUrl();
      if (urlToken) {
        setToken(urlToken);
        setAccessType('user');
      }
    }
  }, [token, setToken]);

  const containerRef = useRef(null);
  const greatNewsShownRef = useRef(false);
  const isProcessingClickRef = useRef(false);
  const skipPaymentConfirmedRef = useRef(false);

  useEffect(() => {
    greatNewsShownRef.current = false;
    setDoubleClickStatus('waiting');
  }, [currentStepId]);

  const { savedAddress, setSavedAddress } = useWorkflow();
  const [addressId, setAddressId] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState({});

  const [selectedProduct, setSelectedProduct] = useState(10);
  const [productList, setProductList] = useState([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [autoHideDuration, setAutoHideDuration] = useState(4000);
  const [skipPaymentDialogOpen, setSkipPaymentDialogOpen] = useState(false);
  const [pendingSkipPaymentEvent, setPendingSkipPaymentEvent] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState({});
  const [selectedAddOnsData, setSelectedAddOnsData] = useState(null);
  const [modalTitle, setModalTitle] = useState('');
  const [modalStatus, setModalStatus] = useState(false);

  const [guarantorId, setGuarantorId] = useState('');
  const [subscriberId, setSubscriberId] = useState('');
  const [accountId, setAccountId] = useState('');
  const [emailId, setEmailId] = useState('');
  const [firstname, setFirstname] = useState('');
  const [lastname, setLastname] = useState('');
  const [personId, setPersonId] = useState('');
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isAddressSet, setIsAddressSet] = useState(false);
  const [scheduledInstallationDate, setScheduledInstallationDate] =
    useState(null);

  // Calculate discount based on autopay and paperless settings
  const calculateDiscount = () => {
    let discount = 0;
    if (autoPayValue === 'yes') discount += 5;
    if (paperlessValue === 'yes') discount += 5;
    return discount;
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleSkipPaymentConfirm = () => {
    console.log('✅ User confirmed skip payment');
    setSkipPayment(true);
    skipPaymentConfirmedRef.current = true;
    setSkipPaymentDialogOpen(false);
    setPendingSkipPaymentEvent(null);

    // Proceed to next step
    const currentIndex = orderedNodes.findIndex(
      (node) => node.id === currentStepId
    );
    if (currentIndex >= 0 && currentIndex < orderedNodes.length - 1) {
      const nextNode = orderedNodes[currentIndex + 1];
      console.log('➡️ Moving to next node:', nextNode.type);
      setCurrentStepId(nextNode.id);
      setTemplateHtml('');
      setAddressSuggestions([]);
      setContinue(nextNode.type, orderedNodes.length - (currentIndex + 2));
    }
  };

  const handleSkipPaymentCancel = () => {
    console.log('❌ User cancelled skip payment - staying on current page');
    setSkipPaymentDialogOpen(false);
    setPendingSkipPaymentEvent(null);
    // Reset the ref so dialog can show again if needed
    skipPaymentConfirmedRef.current = false;
    // Don't navigate - stay on current page
  };

  // ==================== COMPANY-SPECIFIC FUNCTIONS ====================
  const navigateToSignIn = () => {
    const env = process.env.REACT_APP_ENV || 'dev';
    const targetUrl = COMPANY_CONFIG.signInUrl(env);

    // Check if we're in an iframe
    const isInIframe = window.self !== window.top;

    if (isInIframe) {
      try {
        // Try to navigate the parent window
        window.parent.location.href = targetUrl;
      } catch (e) {
        // If blocked by cross-origin policy, open in new tab
        window.open(targetUrl, '_blank');
      }
    } else {
      // Normal navigation
      window.location.href = targetUrl;
    }
  };

  const getApiCustomerId = () => {
    return COMPANY_CONFIG.apiCustomerId;
  };

  const getCompanyOrgName = () => {
    return COMPANY_CONFIG.accountOrgName;
  };

  // Helper function to capitalize first letter
  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  // ==================== HOOKS ====================
  useTriggerAction(ELEMENT_IDS.CHECKOUT_PROCEED_BUTTON, () => {
    handleCheckoutProceedPress();
  });

  useTriggerAction(ELEMENT_IDS.PRODUCT_SELECTION, () => {
    //handleProductService();
  });

  useToggleProperty([
    {
      sourceId: ELEMENT_IDS.ADDRESS_INPUT,
      targetId: ELEMENT_IDS.NEXT_STEP_BUTTON,
      validate: (el) => {
        const isValidInput = el.value.trim() !== '';
        const comingSoonDesc =
          containerRef.current.querySelector('.coming-soon');
        const isComingSoonVisible =
          comingSoonDesc && comingSoonDesc.style.display !== 'none';
        const hasSuggestions = addressSuggestions.length > 0;
        return isValidInput && isAddressSet;
      },
      property: 'disabled',
      valueIfInvalid: true,
      eventName: 'input',
    },
  ]);

  useTriggerAction(ELEMENT_IDS.PREVIOUS_STEP_BUTTON, () => {
    const currentIndex = orderedNodes.findIndex(
      (node) => node.id === currentStepId
    );
    if (currentIndex > 0) {
      const successalertBlock = containerRef.current?.querySelector(
        '.address-success-message'
      );
      if (successalertBlock) {
        successalertBlock.remove();
      }

      greatNewsShownRef.current = false;

      const formRows = containerRef.current?.querySelectorAll(
        '.checkForServiceNode-formRow'
      );
      if (formRows) {
        formRows.forEach((row) => {
          row.style.display = 'flex';
        });
      }

      const previousNode = orderedNodes[currentIndex - 1];
      setCurrentStepId(previousNode.id);
      setTemplateHtml('');
      setAddressSuggestions([]);
      setContinue(previousNode.type, orderedNodes.length - currentIndex);
      window.location.href = window.location.href;
    }
  });

  useTriggerAction(
    [ELEMENT_IDS.SKIP_BUTTON, ELEMENT_IDS.NEXT_STEP_BUTTON],
    async (event) => {
      const clickedId =
        event?.currentTarget?.dataset?.elementId || event?.currentTarget?.id;

      if (clickedId === ELEMENT_IDS.CREATE_ACCOUNT_BUTTON) {
        return;
      }

      const currentNode = orderedNodes.find(
        (node) => node.id === currentStepId
      );

      console.log('🔘 Next button clicked:', {
        nodeType: currentNode?.type,
        company: COMPANY_CONFIG.name,
        requireDoubleClick:
          COMPANY_CONFIG.features.requireDoubleClickForAddress,
        clickedId,
      });

      // ==================== COMPANY-SPECIFIC: ADDRESS DOUBLE-CLICK LOGIC ====================
      if (
        currentNode?.type === 'checkForServiceNode' &&
        clickedId === ELEMENT_IDS.NEXT_STEP_BUTTON &&
        COMPANY_CONFIG.features.requireDoubleClickForAddress
      ) {
        console.log('🔄 Executing double-click logic for ARCHTOP');
        if (event) {
          event.preventDefault();
          event.stopPropagation();
        }

        if (isProcessingClickRef.current) {
          if (serviceAddress && serviceAddress.addressLine1) {
            const addressString = `${serviceAddress.addressLine1}, ${serviceAddress.addressCityName}, ${serviceAddress.addressStateAbbr} ${serviceAddress.addressPostalCode?.slice(0, 5)}`;
            const streetAddressInput = containerRef.current.querySelector(
              '#' + ELEMENT_IDS.ADDRESS_INPUT
            );
            if (streetAddressInput) {
              streetAddressInput.value = addressString;
            }
          }
          return;
        }

        if (!greatNewsShownRef.current) {
          console.log(
            '🎯 ARCHTOP: First Next click - showing Great News message'
          );
          setDoubleClickStatus('first-click');
          isProcessingClickRef.current = true;

          // Hide form rows (apt, city, state, zip fields)
          const formRowsToHide = containerRef.current.querySelectorAll(
            '.checkForServiceNode-formRow'
          );
          console.log('📝 Hiding form rows:', formRowsToHide.length);

          if (formRowsToHide.length > 0) {
            formRowsToHide.forEach((row) => {
              row.style.display = 'none';
            });
          }

          // Update street address input to show full address
          const streetAddressInput = containerRef.current?.querySelector(
            '#' + ELEMENT_IDS.ADDRESS_INPUT
          );
          if (streetAddressInput && serviceAddress) {
            const fullAddress = `${serviceAddress.addressLine1}, ${serviceAddress.addressCityName}, ${serviceAddress.addressStateAbbr} ${serviceAddress.addressPostalCode?.slice(0, 5)}`;
            streetAddressInput.value = fullAddress;
            console.log(
              '✅ Updated street address to full address:',
              fullAddress
            );
          }

          // Show "Great News!" message
          const parentElement = containerRef.current?.querySelector(
            'input[class*="address"]'
          )?.parentNode;

          let alertBlock = containerRef.current?.querySelector(
            '.address-success-message'
          );

          if (!alertBlock && parentElement) {
            alertBlock = document.createElement('div');
            alertBlock.className = 'address-success-message';
            parentElement.appendChild(alertBlock);
            console.log('✅ Created Great News alert block');
          }

          if (alertBlock) {
            alertBlock.innerHTML = `
              <img src="/images/icons/successlikeicon.png" alt="Success" class="success-icon" />
              <span><strong>Great News!</strong> We can provide service at your address.</span>
            `;
            alertBlock.classList.add('show');
            alertBlock.style.display = 'block';
            console.log('🎉 Great News message displayed for ARCHTOP');
          }

          greatNewsShownRef.current = true;

          setTimeout(() => {
            isProcessingClickRef.current = false;
            setDoubleClickStatus('ready-for-second');
            console.log('⏰ Ready for second Next click');
          }, 300);

          return;
        }

        console.log('🎯 ARCHTOP: Second Next click - proceeding to next step');
        setDoubleClickStatus('proceeding');
        isProcessingClickRef.current = true;
      }

      console.log(
        '➡️ Proceeding to next step (single-click or after double-click)'
      );

      const currentIndex = orderedNodes.findIndex(
        (node) => node.id === currentStepId
      );
      if (currentIndex >= 0 && currentIndex < orderedNodes.length - 1) {
        const nextNode = orderedNodes[currentIndex + 1];
        console.log('✅ Moving to next node:', nextNode.type);

        setCurrentStepId(nextNode.id);
        setTemplateHtml('');
        setAddressSuggestions([]);

        setContinue(nextNode.type, orderedNodes.length - (currentIndex + 2));
      } else {
        console.log('⚠️ Cannot proceed - at last step or invalid index');
      }
    }
  );

  const fetchWorkflow = useCallback(async () => {
    setIsLoadingWorkflow(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/workflows/workflow/${workflowId}`,
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      );

      if (response.status === 200) {
        const data = response.data;
        setWorkflow(data.workflow);
        if (
          data.workflow &&
          data.workflow.nodes.length > 0 &&
          data.workflow.edges.length > 0
        ) {
          const ordered = getOrderedNodes(
            data.workflow.nodes,
            data.workflow.edges
          );
          setOrderedNodes(ordered);
          setCurrentStepId(nodeId || ordered[0]?.id || null);
        }
      } else {
        console.error('Failed to fetch workflow');
      }
    } catch (error) {
      console.error('Error fetching workflow:', error);
    } finally {
      setIsLoadingWorkflow(false);
    }
  }, [workflowId, nodeId, token]);

  useEffect(() => {
    console.log('🔍 isAddressSet changed:', isAddressSet);
  }, [isAddressSet]);

  useEffect(() => {
    fetchWorkflow();
  }, [fetchWorkflow]);

  useEffect(() => {
    const subscriber = allSubscriberDetails?.subscribers?.[0];
    const service = subscriber?.services?.[0];
    const guarantor = subscriber?.guarantors?.[0];

    const updatedPaymentDetails = {
      firstName: subscriber?.firstName ?? '',
      lastName: subscriber?.lastName ?? '',
      subscriberId: subscriber?.subscriberId ?? '',
      phoneNumber: subscriber?.subscriberId ?? '',
      email: emailId ?? '',
      accountName: service?.accountName ?? '',
      accountServiceId: service?.accountServiceId ?? '',
      activeSessionId: service?.activeSessionId ?? '',
      addressCityName: service?.addressCityName ?? '',
      addressEffectiveThru: service?.addressEffectiveThru ?? '',
      addressId: service?.addressId ?? '',
      addressLatitude: service?.addressLatitude ?? '',
      addressLine1: service?.addressLine1 ?? '',
      addressLine2: service?.addressLine2 ?? '',
      addressLine3: service?.addressLine3 ?? '',
      addressLongitude: service?.addressLongitude ?? '',
      addressPostalCode: service?.addressPostalCode ?? '',
      addressStateAbbr: service?.addressStateAbbr ?? '',
      autoPayMethod: guarantor?.autoPayMethod ?? '',
      creditClass: guarantor?.creditClass ?? 'A',
      guarantorType: guarantor?.guarantorType ?? '',
      noChecks: guarantor?.noChecks ?? '',
      noCreditCards: guarantor?.noCreditCards ?? '',
      profileId: service?.profileId ?? '',
      guarantorId: guarantor?.guarantorId ?? '',
      CityName: service?.addressCityName ?? '',
      StateName: service?.addressStateAbbr ?? '',
      postalCode: service?.addressPostalCode ?? '',
      CountryName: 'USA',
      autoPayMethodSC: '',
      CreditCardBrand: '',
      CreditCardNumber: '',
      CreditCardExpiration: '',
      ExternalAccountToken: '',
      paymentAmount:
        selectedProduct?.price?.toString().replace('$', '').trim() || '0.00',
      paymentOperation: 'SALE',
      paymentCode: 'PAYMENTUS',
      paymentTypeCode: 'PHONE',
      eftSuspended: 'N',
      paymentMethod: '',
      paymentMethodCategory: '',
      balanceGroup: '',
      payToken: '',
      creditCardType: '',
      creditCardNumberLast4: '',
      creditCardExpirationMonth: '',
      creditCardExpirationYear: '',
      bankRoute: '',
      bankAccountLast4: '',
      bankAccountType: '',
      bankAccountClass: '',
    };

    setNewPaymentDetails(updatedPaymentDetails);
  }, [allSubscriberDetails]);

  const GuarantorUpdateApi = async (GuarantorUpdateData, accessToken) => {
    const requestData = {
      eftUpdate: 'Y',
      subscriberId: GuarantorUpdateData.subscriberId,
      guarantorId: GuarantorUpdateData.guarantorId,
      noChecks: GuarantorUpdateData.noChecks,
      noCreditCards: GuarantorUpdateData.noCreditCards,
      creditClass: GuarantorUpdateData.creditClass,
      treatmentPlanId: '',
      addressNameLine1: '',
      addressNameLine2: '',
      addressLine1: GuarantorUpdateData.addressLine1,
      addressLine2: GuarantorUpdateData.addressLine2,
      addressLine3: GuarantorUpdateData.addressLine3,
      addressCityId: '',
      jobTitle: '',
      monthsEmployed: '',
      selfEmployed: '',
      employerContactName: '',
      employerPhoneNumber: '',
      employerEmailAddress: '',
      employerAddressName: '',
      employerAddressLine1: '',
      employerAddressLine2: '',
      employerAddressCity: '',
      employerStateProvId: '',
      employerPostalCode: '',
      employerCountryId: '',
      residenceOwnOrRent: '',
      residenceMonths: '',
      landlordName: '',
      landlordContactName: '',
      landlordPhoneNumber: '',
      landlordEmailAddress: '',
      eftPaymentMethod: GuarantorUpdateData.eftPaymentMethod,
      eftDivisionId: '1',
      eftAuthNameStart: GuarantorUpdateData.firstName,
      eftAuthNameEnd: GuarantorUpdateData.lastName,
      eftSuspended: GuarantorUpdateData.eftSuspended,
      eftAccountType: GuarantorUpdateData.guarantorType,
      eftAccountFirstName: GuarantorUpdateData.firstName,
      eftAccountMiddleInit: '',
      eftAccountLastName: GuarantorUpdateData.lastName,
      eftAccountOrgName: getCompanyOrgName(), // 🔥 Dynamic company name
      eftCreditCardBrand: GuarantorUpdateData.eftCreditCardBrand,
      eftCreditCardNumber: GuarantorUpdateData.eftCreditCardNumber,
      eftCreditCardExpiration: GuarantorUpdateData.eftCreditCardExpiration,
      eftBankPhoneNumber: GuarantorUpdateData.phoneNumber,
      eftExternalAccountToken: GuarantorUpdateData.eftExternalAccountToken,
      eftBankAuthMethod: 'I',
      eftBankContactType: 'H',
    };

    try {
      const api = `${process.env.REACT_APP_ACCOUNT_MANAGEMETN_URL}/subscriber/${requestData.subscriberId}/guarantor/${requestData.guarantorId}/update`;

      const data = await directApiRequest(
        api,
        HTTP_METHODS.POST,
        requestData,
        accessToken,
        {}
      );
    } catch (error) {
      console.error('Failed to send payment request:', error);
    }
  };

  const GuarantorProcessPaymentApi = async (
    GuarantorProcessPaymentData,
    accessToken
  ) => {
    const requestData = {
      subscriberId: GuarantorProcessPaymentData.subscriberId,
      guarantorId: GuarantorProcessPaymentData.guarantorId,
      profileId: GuarantorProcessPaymentData.profileId,
      paymentAmount: GuarantorProcessPaymentData.paymentAmount,
      paymentCode: GuarantorProcessPaymentData.paymentCode,
      paymentMethod: GuarantorProcessPaymentData.paymentMethod,
      payToken: GuarantorProcessPaymentData.payToken,
      paymentDate: GuarantorProcessPaymentData.paymentDate,
      balanceGroup: GuarantorProcessPaymentData.balanceGroup,
      creditCardType: GuarantorProcessPaymentData.creditCardType,
      creditCardNumberLast4: GuarantorProcessPaymentData.creditCardNumberLast4,
      creditCardExpirationMonth:
        GuarantorProcessPaymentData.creditCardExpirationMonth,
      creditCardExpirationYear:
        GuarantorProcessPaymentData.creditCardExpirationYear,
      bankRoute: GuarantorProcessPaymentData.bankRoute,
      bankAccountLast4: GuarantorProcessPaymentData.bankAccountLast4,
      bankAccountType: GuarantorProcessPaymentData.bankAccountType,
      bankAccountClass: GuarantorProcessPaymentData.bankAccountClass,
      emailAddress: GuarantorProcessPaymentData.emailAddress,
    };

    try {
      const api = `${process.env.REACT_APP_ACCOUNT_MANAGEMETN_URL}/subscriber/${requestData.subscriberId}/guarantor/${requestData.guarantorId}/guarantorProcessPayment`;

      const data = await directApiRequest(
        api,
        HTTP_METHODS.POST,
        requestData,
        accessToken,
        {}
      );

      return data;
    } catch (error) {
      console.error('Failed to send payment request:', error);
      throw error;
    }
  };

  useEffect(() => {
    const fetchTemplateHtml = async () => {
      if (workflowId && currentStepId) {
        setIsLoadingTemplate(true);
        const payload = {
          deleted_at: null,
          wfw_id: workflowId,
          wfw_node_id: currentStepId,
        };
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_BASE_URL}/portalbuilder/list`,
            payload,
            {
              headers: {
                'Content-Type': 'application/json',
                Authorization: `${token}`,
              },
            }
          );
          let resp_data = response.data[0];
          if (
            response.status === 200 &&
            resp_data.deleted_at == null &&
            resp_data.template_status === 'publish'
          ) {
            const updatedTemplateHtml = resp_data.template.replace(
              '{{savedAddress}}',
              savedAddress
            );
            setTemplateHtml(DOMPurify.sanitize(updatedTemplateHtml));
          } else {
            setTemplateHtml(DOMPurify.sanitize(getNoContentMessage()));
          }
        } catch (error) {
          console.error('Error fetching template HTML:', error);
          setTemplateHtml(DOMPurify.sanitize(getNoContentMessage()));
        } finally {
          setIsLoadingTemplate(false);
        }
      }
    };

    fetchTemplateHtml();
  }, [workflowId, currentStepId, token, reloadKey]);

  const fetchPaymentusAccessToken = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_GET_PAYMENTUS_ACCESSTOKEN}/payment/paymentus/get-paymentus-access-token`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        }
      );
      if (!response.ok)
        throw new Error(`Token fetch failed: ${response.status}`);
      return await response.json();
    } catch (error) {
      console.error('Token Fetch Error:', error);
      throw error;
    }
  };

  const makePayment = async (paymentjwttoken, paymentPayload) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_MAKE_PAYMENT}/payment/paymentus/make-payment`,
        {
          method: 'POST',
          headers: {
            paymentusAccessToken: paymentjwttoken,
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
          body: JSON.stringify(paymentPayload),
        }
      );
      if (!response.ok) throw new Error('Payment request failed');
      return await response.json();
    } catch (error) {
      console.error('Payment Error:', error);
      throw error;
    }
  };

  const sendPaymentRequest = async (paymentDetails) => {
    const newPaymentData = MakePaymentApiDetails(paymentDetails);

    try {
      const { token } = await fetchPaymentusAccessToken();

      const paymentResult = await makePayment(token, newPaymentData);

      const paymentStatus =
        paymentResult?.['payment-response']?.['payment-status'];
      const paymentDate = paymentResult?.['payment-response']?.['payment-date'];
      const payAmount =
        newPaymentData?.payment?.header?.['payment-amount'] || 0;

      if (paymentStatus === 'ACCEPTED') {
        paymentDate && generateDateTime(paymentDate);
        setIframeVisible(false);
      } else {
        setIframeVisible(false);
      }
    } catch (error) {
      console.error('Payment Request Error:', error);
    }
  };

  const handlePaymentusCallBackData = async (
    autopay,
    savecard,
    result,
    subscriptionDetailsRes
  ) => {
    try {
      const api = `${process.env.REACT_APP_PAYMENT_BASE_URL}/payment/paymentus/get-pay-token`;

      const requestData = {
        subscriberId: result.subscriberId,
        externalReference: externalReference,
        saveCard: true,
      };

      const response = await fetch(api, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `${token}`,
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) throw new Error(`HTTP Error: ${response.status}`);

      const responseJson = await response.json();

      if (responseJson?.data?.Token) {
        const data = responseJson.data;

        const isBank = ['CHQ', 'SAV'].includes(data.Type);
        const isCard = [
          'VISA',
          'MASTERCARD',
          'AMEX',
          'DISCOVER',
          'VI',
          'MC',
          'DISC',
        ].includes(data.Type);

        const [expMonth = '', expYear = ''] = data.ExpiryDate?.split('/') ?? [];

        const updatedPaymentDetails = {
          ...newPaymentDetails,
          firstName: subscriptionDetailsRes?.subscribers?.[0]?.firstName ?? '',
          lastName: subscriptionDetailsRes?.subscribers?.[0]?.lastName ?? '',
          subscriberId:
            subscriptionDetailsRes?.subscribers?.[0]?.subscriberId ?? '',
          phoneNumber:
            subscriptionDetailsRes?.subscribers?.[0]?.subscriberId ?? '',

          accountName:
            subscriptionDetailsRes?.subscribers?.[0]?.services?.[0]
              ?.accountName ?? '',
          accountServiceId:
            subscriptionDetailsRes?.subscribers?.[0]?.services?.[0]
              ?.accountServiceId ?? '',
          addressLine1:
            subscriptionDetailsRes?.subscribers?.[0]?.services?.[0]
              ?.addressLine1 ?? '',
          addressLine2:
            subscriptionDetailsRes?.subscribers?.[0]?.services?.[0]
              ?.addressLine2 ?? '',
          addressLine3:
            subscriptionDetailsRes?.subscribers?.[0]?.services?.[0]
              ?.addressLine3 ?? '',
          postalCode:
            subscriptionDetailsRes?.subscribers?.[0]?.services?.[0]
              ?.addressPostalCode ?? '',
          StateName:
            subscriptionDetailsRes?.subscribers?.[0]?.services?.[0]
              ?.addressStateAbbr ?? '',
          CityName:
            subscriptionDetailsRes?.subscribers?.[0]?.services?.[0]
              ?.addressCityName ?? '',
          CountryName: 'USA',
          guarantorId:
            subscriptionDetailsRes?.subscribers?.[0]?.guarantors?.[0]
              ?.guarantorId ?? '',

          paymentAmount:
            selectedProduct?.price?.toString().replace('$', '').trim() ||
            '0.00',
          profileId:
            subscriptionDetailsRes?.subscribers?.[0]?.services?.[0]
              ?.profileId ?? '',

          paymentOperation: 'SALE',
          paymentCode: 'PAYMENTUS',
          paymentTypeCode: getPaymentTypeCode(data.Type ?? ''),
          autoPayMethodSC: '',
          balanceGroup: '',
          payToken: data.Token ?? '',
          ExternalAccountToken: data.ExternalReference ?? '',
          CreditCardNumber: data.MaskedAccountNumber ?? '',
          CardHolderName: data.CardHolderName ?? '',
          creditCardNumberLast4: isCard
            ? (data.MaskedAccountNumber?.slice(-4) ?? '')
            : '',

          creditCardType: isCard ? (data.Type ?? '') : '',
          creditCardExpirationMonth: isCard ? expMonth : '',
          creditCardExpirationYear: isCard ? expYear : '',
          CreditCardBrand: isCard ? (data.Type ?? '') : '',
          CreditCardExpiration: isCard ? (data.ExpiryDate ?? '') : '',

          bankRoute: isBank ? (data.RoutingNumber ?? '') : '',
          bankAccountType: isBank ? (data.Type === 'CHQ' ? 'C' : 'S') : '',
          bankAccountClass: '',
          bankAccountLast4: isBank
            ? (data.MaskedAccountNumber?.slice(-4) ?? '')
            : '',

          eftCreditCardBrand:
            autopay && isCard ? getCardBrand(data.Type ?? '') : '',
          eftCreditCardNumber:
            autopay && (data.MaskedAccountNumber?.slice(-4) ?? ''),

          eftCreditCardExpiration:
            autopay && generateCardExpiryDateTime(data.ExpiryDate),
          eftExternalAccountToken: autopay && (data.Token ?? ''),

          eftBankAccountType:
            autopay && isBank ? (data.Type === 'CHQ' ? 'C' : 'S') : '',
          eftBankRoutingNumber: autopay && (data.RoutingNumber ?? ''),
          eftBankAccountNumber:
            autopay && (data.MaskedAccountNumber?.slice(-4) ?? ''),
          eftBankAuthMethod: autopay && 'I',

          eftPaymentMethod: autopay && (isBank ? 'ECP' : 'EFT-CC'),

          paymentMethod: isBank ? 'Check' : isCard ? 'CreditCard' : '',
        };
        setNewPaymentDetails(updatedPaymentDetails);

        if (autopay) GuarantorUpdateApi(updatedPaymentDetails, token);
        else {
          sendPaymentRequest(updatedPaymentDetails);
          GuarantorProcessPaymentApi(updatedPaymentDetails, token);
        }
      } else {
        if (PAYMENT_AVAILABLE) {
          setSnackbarMessage('Payment failed!');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        }
      }
    } catch (error) {
      if (PAYMENT_AVAILABLE) {
        setSnackbarMessage('Payment failed!');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    }
  };

  // =========================== START ACCOUNT SIGNUP NODE ===========================
  useEffect(() => {
    if (!containerRef.current || !workflow?.nodes) return;

    const filteredNode = workflow.nodes.find(
      (node) => node.id === currentStepId
    );
    if (!filteredNode || filteredNode.type !== 'accountSetupNode') return;

    const container = containerRef.current;

    setValidationFlag(false);
    const checkboxLabel = container.querySelector('.checkbox-label');
    if (checkboxLabel) {
      checkboxLabel.innerHTML = checkboxLabel.innerHTML.replace(
        'internet',
        selectedService.serviceTypeDescription
      );
    }

    const addressField = container.querySelector('#account-address');
    if (addressField) {
      addressField.value = savedAddress;
      addressField.setAttribute('readOnly', true);
    }

    const phoneInput = container.querySelector(`#${ELEMENT_IDS.ACCOUNT_PHONE}`);

    if (phoneInput) {
      let phoneError = container.querySelector('#account-phone-error');
      if (!phoneError && phoneInput.parentNode) {
        phoneError = document.createElement('p');
        phoneError.id = 'account-phone-error';
        phoneError.style.color = 'red';
        phoneError.style.marginTop = '4px';
        phoneError.style.fontSize = '0.9rem';
        phoneError.style.display = 'none';
        phoneInput.parentNode.appendChild(phoneError);
      }

      const formatPhone = (val) => {
        const digits = val.replace(/\D/g, '').slice(0, 10);
        const len = digits.length;
        if (len < 4) return digits;
        if (len < 7) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
      };

      const validatePhone = (val) => /^\d{10}$/.test(val.replace(/\D/g, ''));

      phoneInput.addEventListener('input', (e) => {
        const formatted = formatPhone(e.target.value);
        e.target.value = formatted;

        if (phoneError) {
          phoneError.textContent = '';
          phoneError.style.display = 'none';
        }
      });

      phoneInput.addEventListener('blur', (e) => {
        const raw = e.target.value.replace(/\D/g, '');

        // Validate the raw digits directly here, don't call validatePhone()
        if (raw.length !== 10) {
          if (phoneError) {
            phoneError.textContent =
              'Please enter a valid 10-digit phone number.';
            phoneError.style.display = 'block';
          }
        } else {
          if (phoneError) {
            phoneError.textContent = '';
            phoneError.style.display = 'none';
          }
        }
      });
    }

    const togglePasswordVisibility = (inputId, toggleBtnId) => {
      const input = container.querySelector(`#${inputId}`);
      const toggleBtn = container.querySelector(`#${toggleBtnId}`);
      if (!input || !toggleBtn) return;

      const eye = `<svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" stroke-linejoin="round" stroke-linecap="round" viewBox="0 0 24 24" stroke-width="2" fill="none" stroke="currentColor"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle r="3" cy="12" cx="12"></circle></svg>`;

      const eyeOff = `<svg stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>`;

      toggleBtn.addEventListener('click', () => {
        const isHidden = input.type === 'password';
        input.type = isHidden ? 'text' : 'password';
        toggleBtn.innerHTML = isHidden ? eyeOff : eye;
      });
    };

    togglePasswordVisibility(
      'account-password',
      'account-toggle-password_main'
    );
    togglePasswordVisibility(
      'account-confirm-password',
      'account-toggle-password_confirm'
    );

    const createAcBtn = container.querySelector('#create-account-button');
    const passwordInput = container.querySelector(
      `#${ELEMENT_IDS.ACCOUNT_PASSWORD}`
    );
    const confirmPasswordInput = container.querySelector(
      `#${ELEMENT_IDS.ACCOUNT_CONFIRM_PASSWORD}`
    );

    if (createAcBtn) {
      if (!container.querySelector('#signup_error_bock')) {
        const newParagraph = document.createElement('p');
        newParagraph.id = 'signup_error_bock';
        newParagraph.textContent = '';
        createAcBtn.insertAdjacentElement('afterend', newParagraph);
      }

      const handleSignup = () => {
        const getInputValue = (id) =>
          container.querySelector(`#${id}`)?.value?.trim() || '';

        const firstName = getInputValue(ELEMENT_IDS.ACCOUNT_FIRST_NAME);
        const lastName = getInputValue(ELEMENT_IDS.ACCOUNT_LAST_NAME);
        const mobilePhone = getInputValue(ELEMENT_IDS.ACCOUNT_PHONE);
        const emailAddress = getInputValue(ELEMENT_IDS.ACCOUNT_EMAIL);
        const password = getInputValue(ELEMENT_IDS.ACCOUNT_PASSWORD);
        const confirmPassword = getInputValue(
          ELEMENT_IDS.ACCOUNT_CONFIRM_PASSWORD
        );
        const serviceAddress = getInputValue(ELEMENT_IDS.ACCOUNT_ADDRESS);
        const lowerPassword = password.toLowerCase();
        const lowerFirstName = firstName.toLowerCase();
        const lowerLastName = lastName.toLowerCase();

        const isValidEmail = (email) =>
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        const isValidPhoneNumber = (number) =>
          /^\d{10}$/.test(number.replace(/\D/g, ''));
        const passwordRegex =
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

        setMobileNumber(mobilePhone);

        setSnackbarSeverity('error');
        setSnackbarOpen(true);

        if (!firstName)
          return setSnackbarMessage('Please enter your first name.');
        if (!lastName)
          return setSnackbarMessage('Please enter your last name.');
        if (!mobilePhone)
          return setSnackbarMessage('Please enter your mobile phone number.');
        if (!isValidPhoneNumber(mobilePhone))
          return setSnackbarMessage(
            'Please enter a valid mobile phone number.'
          );
        if (!emailAddress)
          return setSnackbarMessage('Please enter your email address.');
        if (!isValidEmail(emailAddress))
          return setSnackbarMessage('Please enter a valid email address.');
        if (!password) return setSnackbarMessage('Please enter a password.');
        if (!passwordRegex.test(password))
          return setSnackbarMessage(
            'Password must be at least 8 characters long, include uppercase, lowercase, a number, and a special character.'
          );
        if (
          lowerPassword.includes(lowerFirstName) ||
          lowerPassword.includes(lowerLastName)
        ) {
          return setSnackbarMessage(
            'Password should not contain your first name or last name.'
          );
        }
        if (!serviceAddress)
          return setSnackbarMessage('Please enter your service address.');
        if (password !== confirmPassword) {
          confirmPasswordInput.classList.add('invalid-password');
          return setSnackbarMessage('Passwords do not match.');
        }

        const getCurrentDateFormatted = () => {
          const now = new Date();
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          return `${year}-${month}-${day}-00.00.00`;
        };

        const payload = {
          accountStructureId: SELF_SIGNUP_PAYLOAD.accountStructureId,
          invoiceDeliveryMethod:
            paperlessValue === 'yes' ? SELF_SIGNUP_PAYLOAD.internet : SELF_SIGNUP_PAYLOAD.paper,
          subscriberType: subscriberType,
          serviceAddressId: addressId,
          activationDate: getCurrentDateFormatted(),
          personLanguageId: SELF_SIGNUP_PAYLOAD.personLanguageId,
          personFirstName: firstName,
          personLastName: lastName,
          personBirthDate: SELF_SIGNUP_PAYLOAD.personBirthDate,
          sendEcareRegistrationEmail: SELF_SIGNUP_PAYLOAD.sendEcareRegistrationEmail,
          creditClass: SELF_SIGNUP_PAYLOAD.creditClass,
          ecareLogin: emailAddress,
          ecarePassword: password,
          ecareEmailAddress: emailAddress,
          ecareFirstName: firstName,
          ecareLastName: lastName,
          accountType: accountType,
          invoiceChargeMethod: SELF_SIGNUP_PAYLOAD.invoiceChargeMethod,
          paymentTermId:
            COMPANY_CONFIG.domain === 'prtc'
              ? SELF_SIGNUP_PAYLOAD.paymentTerm.paymentTermId
              : COMPANY_CONFIG.domain === 'archtop'
                ? SELF_SIGNUP_PAYLOAD.paymentTerm.standard
                : COMPANY_CONFIG.domain === 'cwf'
                  ? SELF_SIGNUP_PAYLOAD.paymentTerm.basic
                  : SELF_SIGNUP_PAYLOAD.paymentTerm.paymentTermId,
          productId: expandedProduct.productId,
          productCode: expandedProduct.productCode,
          ISPinitialPassword: SELF_SIGNUP_PAYLOAD.ISPinitialPassword,
          completeSession: SELF_SIGNUP_PAYLOAD.completeSession,
        };
        setSignUpPayload(payload);

        const signUp = async () => {
          try {
            let val = parseInt(createAcBtn.getAttribute('data-status'));
            createAcBtn.setAttribute('data-status', val + 1);
            setAutoHideDuration(20000);
            setSnackbarSeverity('success');
            setSnackbarMessage('Processing ...');

            setIsLoading(true);
            const response = await fetch(
              `${process.env.REACT_APP_BASE_URL}/workflows/create/self-signup`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `${token}`,
                },
                body: JSON.stringify(payload),
              }
            );
            if (!response.ok) {
              //throw new Error('Failed to sign up. Status: ' + response.status);
              setSnackbarSeverity('error');
              setSnackbarMessage(
                'Failed to sign up. Status: ' + response.status
              );
            }

            const result = await response.json();
            val = parseInt(createAcBtn.getAttribute('data-status'));
            if (result.returnMessage) {
              setSnackbarSeverity('error');
              setSnackbarMessage(result.returnMessage);
              return;
            } else if (result.message) {
              setSnackbarSeverity('error');
              setSnackbarMessage(result.message);
              return;
            }
            if (result.returnCode === 'BATCHCOMPLETE') {
              setSnackbarSeverity('success');
              setSnackbarMessage('Signup successful');
              setGuarantorId(result.guarantorId);
              setSubscriberId(result.subscriberId);
              setPersonId(result.personId);

              const getSubscriberDetails = await fetch(
                `${process.env.REACT_APP_ACCOUNT_MANAGEMENT_URL}/subscriber/${result.subscriberId}`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `${token}`,
                  },
                  body: JSON.stringify({
                    includeContacts: 'Y',
                    includeCharges: 'Y',
                  }),
                }
              );
              if (!getSubscriberDetails.ok) {
                throw new Error(
                  'Second API call failed. Status: ' +
                    getSubscriberDetails.status
                );
              }

              const subscriptionDetails = await getSubscriberDetails.json();
              setAccountId(
                subscriptionDetails?.subscribers[0]?.services[0]
                  ?.accountServiceId
              );
              setallSubscriberDetails(subscriptionDetails);
              setSetSubscriberDetails(subscriptionDetails);

              if (result && !skipPayment) {
                const autoPayFlag = autoPayValue === 'yes' ? 1 : 0;

                setTimeout(() => {
                  handlePaymentusCallBackData(
                    autoPayFlag,
                    0,
                    result,
                    subscriptionDetails
                  );
                }, 1000);
              }

              setTimeout(() => {
                const container = containerRef.current;
                if (container) {
                  container.innerHTML = renderToHTMLString(
                    <SuccessNode
                      accountId={
                        result?.accountId
                      }
                    />,
                    {
                      minify: true,
                    }
                  );

                  const nextStepBtn =
                    container.querySelector('#next-step-button');
                  if (nextStepBtn) {
                    nextStepBtn.addEventListener('click', () => {
                      handleNext(true);
                    });
                  }
                }
              }, 1000);
            }
          } catch (error) {
            console.error('Error during signup:', error);
            setSnackbarMessage(error.message);
          } finally {
            setIsLoading(false);
            let val = parseInt(createAcBtn.getAttribute('data-status'));
            createAcBtn.setAttribute('data-status', val + 1);
            setAutoHideDuration(4000);
          }
        };

        signUp();
      };

      createAcBtn.setAttribute('data-status', 1);
      createAcBtn.removeEventListener('click', handleSignup);
      createAcBtn.addEventListener('click', () => {
        let val = parseInt(createAcBtn.getAttribute('data-status'));
        if (val % 2 === 1) {
          handleSignup();
        }
      });
    }

    const checklistContainer = container.querySelector(
      '.accountSetupNode-passwordChecklist'
    );
    const checklistItems = checklistContainer?.querySelectorAll('li');

    if (passwordInput && checklistContainer && checklistItems) {
      checklistContainer.style.display = 'none';

      const validatePassword = (password) => {
        const lengthCheck = password.length >= 8;
        const caseCheck = /[a-z]/.test(password) && /[A-Z]/.test(password);
        const specialOrNumberCheck = /[\d@$!%*?&]/.test(password);
        return [lengthCheck, caseCheck, specialOrNumberCheck];
      };

      const updateChecklistUI = (password) => {
        const [lengthCheck, caseCheck, specialOrNumberCheck] =
          validatePassword(password);
        checklistItems[0].style.color = lengthCheck ? '#0D9146' : 'red';
        checklistItems[1].style.color = caseCheck ? '#0D9146' : 'red';
        checklistItems[2].style.color = specialOrNumberCheck
          ? '#0D9146'
          : 'red';
      };

      const handlePasswordInput = (e) => {
        const value = e.target.value;
        if (value && checklistContainer.style.display === 'none') {
          checklistContainer.style.display = 'block';
        }
        updateChecklistUI(value);
        handleConfirmPasswordValidation();
      };

      passwordInput.removeEventListener('input', handlePasswordInput);
      passwordInput.addEventListener('input', handlePasswordInput);
    }

    const handleConfirmPasswordValidation = () => {
      const passwordValue = passwordInput?.value || '';
      const confirmValue = confirmPasswordInput?.value || '';

      if (confirmValue && confirmValue !== passwordValue) {
        confirmPasswordInput.classList.add('invalid-password');
      } else {
        confirmPasswordInput.classList.remove('invalid-password');
      }
    };

    if (confirmPasswordInput) {
      ['input', 'blur'].forEach((event) => {
        confirmPasswordInput.addEventListener(
          event,
          handleConfirmPasswordValidation
        );
      });
    }
  }, [templateHtml, workflow, currentStepId, paperlessValue]);
  // =========================== END ACCOUNT SIGNUP NODE ===========================

  /* Add On node */
  useEffect(() => {
    if (containerRef.current && workflow && workflow.nodes) {
      const filteredNode = workflow.nodes.find(
        (node) => node.id === currentStepId
      );

      if ('addOnsNode' == filteredNode.type) {
        const signUpBtn = containerRef.current.querySelector('.sign-up-button');
        const comingSoonDesc = containerRef.current.querySelector(
          '.coming-soon-description'
        );

        if (comingSoonDesc) {
          comingSoonDesc.innerHTML = comingSoonDesc.innerHTML.replace(
            'internet',
            selectedService.serviceTypeDescription
          );
        }

        if (signUpBtn) {
          signUpBtn.addEventListener('click', (event) => {
            //handleNext();
          });
        }

        // Handle add-on button clicks
        const addOnButtons = containerRef.current.querySelectorAll(
          '.addOnsNode-AddonCard button'
        );
        addOnButtons.forEach((button, index) => {
          const addOnId = index + 1; // Assuming add-on IDs start from 1

          // Update button content (text and icon) based on current state
          const isSelected = selectedAddOns[addOnId];
          updateButtonContent(button, isSelected);

          // Remove existing event listeners to prevent duplicates
          button.replaceWith(button.cloneNode(true));
          const newButton = containerRef.current.querySelectorAll(
            '.addOnsNode-AddonCard button'
          )[index];

          newButton.addEventListener('click', (event) => {
            event.preventDefault();
            const wasSelected = selectedAddOns[addOnId];

            setSelectedAddOns((prev) => ({
              ...prev,
              [addOnId]: !prev[addOnId],
            }));

            // Show toast notification
            const addOnName = 'Wi-Fi Mesh Extender'; // You can make this dynamic based on addOnId
            if (!wasSelected) {
              setSnackbarMessage(`${addOnName} has been added to your plan!`);
              setSnackbarSeverity('success');
            } else {
              setSnackbarMessage(
                `${addOnName} has been removed from your plan.`
              );
              setSnackbarSeverity('info');
            }
            setSnackbarOpen(true);
          });
        });
      }
    }
  }, [templateHtml]);

  // Helper function to update button content (text and icon)
  const updateButtonContent = (button, isSelected) => {
    // Clear button content
    button.innerHTML = '';

    // Add text
    const textNode = document.createTextNode(isSelected ? 'Added' : 'Add');
    button.appendChild(textNode);

    // Create and add icon
    const iconContainer = document.createElement('span');
    iconContainer.style.marginLeft = '8px';
    iconContainer.style.display = 'inline-flex';
    iconContainer.style.alignItems = 'center';

    if (isSelected) {
      // Add check icon
      iconContainer.innerHTML = renderToHTMLString(<Check size={16} />);
    } else {
      // Add plus icon
      iconContainer.innerHTML = renderToHTMLString(<CirclePlus size={16} />);
    }

    button.appendChild(iconContainer);
  };

  // Update add-on button content when selection changes
  useEffect(() => {
    if (templateHtml && workflow && currentStepId) {
      const filteredNode = workflow.nodes.find(
        (node) => node.id === currentStepId
      );

      if ('addOnsNode' == filteredNode.type) {
        const addOnButtons = containerRef.current?.querySelectorAll(
          '.addOnsNode-AddonCard button'
        );
        addOnButtons?.forEach((button, index) => {
          const addOnId = index + 1;
          const isSelected = selectedAddOns[addOnId];
          updateButtonContent(button, isSelected);
        });
      }
    }
  }, [selectedAddOns, templateHtml, workflow, currentStepId]);

  // Update cart when selectedAddOns changes and we're on reviewCartNode
  useEffect(() => {
    if (workflow && currentStepId) {
      const filteredNode = workflow.nodes.find(
        (node) => node.id === currentStepId
      );
      if (filteredNode?.type === 'reviewCartNode') {
        const container = containerRef.current;
        if (container) {
          setTimeout(() => {
            const installDateRaw =
              scheduledInstallationDate || generateScheduledInstallationDate();
            const installDate = formatInstallationMessage(installDateRaw);
            const hasSelectedAddOns =
              Object.values(selectedAddOns).some(Boolean);
            const addonPrice = hasSelectedAddOns ? '$3.95' : '$0.00';
            const addonName = hasSelectedAddOns
              ? 'WI-FI Mesh Extender'
              : 'No Add-ons Selected';

            const basePrice = parsePrice(selectedProduct?.price ?? '$0.00');
            const addOnPrice = hasSelectedAddOns ? 3.95 : 0;
            const totalItems = hasSelectedAddOns ? 2 : 1;
            // Calculate discount based on autopay and paperless settings
            let discount = 0;
            if (autoPayValue === 'yes') discount += 5;
            if (paperlessValue === 'yes') discount += 5;

            console.log('💰 [First useEffect] Discount calculation:', {
              autoPayValue,
              paperlessValue,
              discount,
            });

            const orderValue = basePrice + addOnPrice - discount;

            setValue(container, ELEMENT_IDS.CHECKOUT_ADDON_NAME, addonName);
            setValue(
              container,
              ELEMENT_IDS.CHECKOUT_ADDON_PRICE,
              `${addonPrice}/month`
            );

            // Update discount display
            setValue(
              container,
              ELEMENT_IDS.CHECKOUT_DISCOUNT,
              `-$${discount.toFixed(2)}`
            );

            setValue(
              container,
              ELEMENT_IDS.CHECKOUT_ORDER_TOTAL,
              `$${orderValue.toFixed(2)}`
            );
            setValue(
              container,
              ELEMENT_IDS.CHECKOUT_ORDER_VALUE,
              `$${orderValue.toFixed(2)}`
            );

            // Show/hide add-ons section based on selection
            const addonSection = container
              .querySelector(`#${ELEMENT_IDS.CHECKOUT_ADDON_NAME}`)
              ?.closest('.reviewCartNode-SectionHeaderRow');
            if (addonSection) {
              addonSection.style.display = hasSelectedAddOns ? 'flex' : 'none';
            }

            // Update order summary details
            setValue(
              container,
              ELEMENT_IDS.CHECKOUT_PLAN_NAME_SUMMARY,
              formatProductName(selectedProduct)
            );
            setValue(
              container,
              ELEMENT_IDS.CHECKOUT_PLAN_PRICE_SUMMARY,
              selectedProduct?.price ?? '$0.00'
            );
            setValue(
              container,
              ELEMENT_IDS.CHECKOUT_ADDON_NAME_SUMMARY,
              'WI-FI Mesh Extender'
            );
            setValue(
              container,
              ELEMENT_IDS.CHECKOUT_ADDON_PRICE_SUMMARY,
              '$3.95'
            );

            // Show/hide add-on summary item
            const addonSummaryItem = container.querySelector(
              '#addon-summary-item'
            );
            if (addonSummaryItem) {
              addonSummaryItem.style.display = hasSelectedAddOns
                ? 'flex'
                : 'none';
            }

            console.log('🔄 Cart updated due to add-ons change:', {
              hasSelectedAddOns,
              orderValue,
              installDate: installDate,
              discount,
            });
          }, 200);
        }
      }
    }
  }, [
    selectedAddOns,
    selectedProduct,
    workflow,
    currentStepId,
    autoPayValue,
    paperlessValue,
  ]);

  // Update order confirmation when selectedAddOns changes and we're on orderReceiptNode
  useEffect(() => {
    if (workflow && currentStepId) {
      const filteredNode = workflow.nodes.find(
        (node) => node.id === currentStepId
      );
      if (filteredNode?.type === 'orderReceiptNode') {
        const confirmationContainer = containerRef.current;
        if (confirmationContainer) {
          setTimeout(() => {
            const hasSelectedAddOns =
              Object.values(selectedAddOns).some(Boolean);
            const basePrice = parsePrice(selectedProduct?.price ?? '$0.00');
            const addOnPrice = hasSelectedAddOns ? 3.95 : 0;

            // Calculate discounts based on AutoPay and Paperless settings
            let discount = 0;
            if (autoPayValue === 'yes') discount += 5;
            if (paperlessValue === 'yes') discount += 5;

            const totalPrice = basePrice + addOnPrice - discount;

            // Update total
            const totalElement = confirmationContainer.querySelector(
              '.orderReceiptNode-totalRow div:last-child'
            );
            if (totalElement) {
              totalElement.textContent = `Total: $${totalPrice.toFixed(2)}/mo`;
            }

            // Update monthly charges rows
            const monthlyRows = confirmationContainer.querySelectorAll(
              '.orderReceiptNode-section:nth-of-type(2) .orderReceiptNode-row:not(.orderReceiptNode-header):not(.orderReceiptNode-totalRow)'
            );

            const chargeData = [
              {
                description: formatProductName(selectedProduct),
                price: basePrice.toFixed(2),
              },
              {
                description: 'Modem Rental',
                price: '0.00',
              },
              {
                description: 'Estimated Taxes & Fees',
                price: '0.00',
              },
            ];

            // Add WI-FI Mesh Extender if selected
            if (hasSelectedAddOns) {
              chargeData.splice(1, 0, {
                description: 'WI-FI Mesh Extender',
                price: addOnPrice.toFixed(2),
              });
            }

            // Add discount row if there are discounts
            if (discount > 0) {
              chargeData.push({
                description: 'Discounts',
                price: `-${discount.toFixed(2)}`,
                isDiscount: true,
              });
            }

            monthlyRows.forEach((row, index) => {
              const columns = row.querySelectorAll('div');
              const charge = chargeData[index];
              if (columns.length >= 2 && charge) {
                columns[0].textContent = charge.description ?? '';
                if (columns.length === 3) {
                  columns[1].textContent = '';
                  columns[2].textContent = `$${charge.price}`;
                } else {
                  columns[1].textContent = `$${charge.price}`;
                }
              }
            });

            // Show/hide WI-FI Mesh Extender note
            const meshExtenderNote = confirmationContainer.querySelector(
              '.orderReceiptNode-notes li:last-child'
            );
            if (meshExtenderNote) {
              meshExtenderNote.style.display = hasSelectedAddOns
                ? 'list-item'
                : 'none';
            }

            console.log(
              '🔄 Order confirmation updated due to add-ons change:',
              {
                hasSelectedAddOns,
                totalPrice,
              }
            );
          }, 50);
        }
      }
    }
  }, [selectedAddOns, selectedProduct, workflow, currentStepId]);

  useEffect(() => {
    if (containerRef.current && workflow && workflow.nodes) {
      const filteredNode = workflow.nodes.find(
        (node) => node.id === currentStepId
      );
      if ('addOnsNode' == filteredNode.type) {
        const signUpBtn = containerRef.current.querySelector('.sign-up-button');
        const comingSoonDesc = containerRef.current.querySelector(
          '.coming-soon-description'
        );
        if (comingSoonDesc) {
          comingSoonDesc.innerHTML = comingSoonDesc.innerHTML.replace(
            'internet',
            selectedService.serviceTypeDescription
          );
        }
        if (signUpBtn) {
          signUpBtn.addEventListener('click', (event) => {
            //handleNext();
          });
        }
      }
    }
  }, [templateHtml]);

  function renderPageContainer(innerHTML) {
    return `
    <div class="pageWrapper-background">
      <div class="pageWrapper-fixedTop">
        <div style="height: 100%"></div>
      </div>
      <div class="pageWrapper-scrollArea">
        ${innerHTML}
      </div>
    </div>
  `;
  }

  function renderSuccessScreen(subscriberId) {
    return renderToHTMLString(<SuccessNode subscriberId={subscriberId} />, {
      minify: true,
    });
  }

  function renderTermsAndConditionsNode({ container, onBack }) {
    if (!container) return;

    const rootElement = document.createElement('div');
    container.innerHTML = '';
    container.appendChild(rootElement);

    const root = createRoot(rootElement);
    root.render(<TermsConditionNode onBack={onBack} />);
  }

  const formatDateTime = (date) => {
    const d = new Date(date);
    const month = d.getMonth() + 1;
    const day = d.getDate();
    const year = d.getFullYear();

    let hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;

    return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
  };

  const formatInstallationMessage = (date) => {
    const d = new Date(date);
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];

    const month = months[d.getMonth()];
    const day = d.getDate();
    const year = d.getFullYear();

    let hours = d.getHours();
    const minutes = d.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;

    return `Installation on ${month} ${day}, ${year} ${hours}:${minutes} ${ampm} CDT`;
  };

  const setValue = (container, id, value) => {
    const el = container.querySelector(`#${id}`);
    if (el) el.textContent = value;
  };

  const setInstallationDate = (container, id, value) => {
    const el = container.querySelector(`#${id}`);
    if (el) {
      // Find and preserve the edit button
      const editButton = el.querySelector('.reviewCartNode-IconButton');
      // Set the installation message
      el.textContent = value;
      // Re-append the edit button if it existed
      if (editButton) {
        el.appendChild(editButton);
      }
    }
  };

  // Generate a realistic scheduled installation date (3-7 business days from now)
  const generateScheduledInstallationDate = () => {
    const now = new Date();
    const daysToAdd = Math.floor(Math.random() * 5) + 3; // 3-7 days
    const scheduledDate = new Date(now);
    scheduledDate.setDate(now.getDate() + daysToAdd);

    // Set to business hours (9 AM - 5 PM)
    const businessHours = [9, 10, 11, 13, 14, 15, 16]; // Skip 12 PM (lunch)
    const randomHour =
      businessHours[Math.floor(Math.random() * businessHours.length)];
    const randomMinutes = 0; // Always use :00 times

    scheduledDate.setHours(randomHour, randomMinutes, 0, 0);

    // If it falls on weekend, move to next Monday
    const dayOfWeek = scheduledDate.getDay();
    if (dayOfWeek === 0) {
      // Sunday
      scheduledDate.setDate(scheduledDate.getDate() + 1);
    } else if (dayOfWeek === 6) {
      // Saturday
      scheduledDate.setDate(scheduledDate.getDate() + 2);
    }

    return scheduledDate;
  };

  const parsePrice = (val) => Number(val?.replace(/[^0-9.]/g, '') || 0);

  const formatProductName = (product) => {
    if (!product) return 'Internet Service';

    const title = product.title || product.description || '';

    // Extract speed information from various formats
    // Look for patterns like "500 Mbps", "Download: 500 Mbps Upload: 500 Mbps", "500/500", "1GB", etc.

    // First try to match download/upload patterns with units like "1GB/1GB" or "500Mbps/500Mbps"
    const downloadUploadWithUnitsMatch = title.match(
      /(\d+\s*(?:GB|Mbps|gbps))\s*\/\s*(\d+\s*(?:GB|Mbps|gbps))/i
    );
    if (downloadUploadWithUnitsMatch) {
      const download = downloadUploadWithUnitsMatch[1].replace(/\s+/g, '');
      const upload = downloadUploadWithUnitsMatch[2].replace(/\s+/g, '');
      return `Internet Service ${download}/${upload}`;
    }

    // Try to match download/upload patterns like "500/500" (numbers only)
    const downloadUploadMatch = title.match(/(\d+)\s*\/\s*(\d+)/i);
    if (downloadUploadMatch) {
      const download = downloadUploadMatch[1];
      const upload = downloadUploadMatch[2];
      return `Internet Service ${download}/${upload}`;
    }

    // Try to match single speed with unit like "1GB" or "500 Mbps"
    const singleSpeedWithUnitMatch = title.match(/(\d+\s*(?:GB|Mbps|gbps))/i);
    if (singleSpeedWithUnitMatch) {
      const speed = singleSpeedWithUnitMatch[1].replace(/\s+/g, '');
      return `Internet Service ${speed}/${speed}`;
    }

    // Try to match single speed number like "500"
    const singleSpeedMatch = title.match(/(\d+)/i);
    if (singleSpeedMatch) {
      const speed = singleSpeedMatch[1];
      return `Internet Service ${speed}/${speed}`;
    }

    return 'Internet Service';
  };

  /* Checkout node */
  useEffect(() => {
    if (containerRef.current && workflow && workflow.nodes) {
      const container = containerRef.current;
      const filteredNode = workflow.nodes.find(
        (node) => node.id === currentStepId
      );

      if (filteredNode?.type === 'reviewCartNode') {
        // Generate installation date only once when first visiting reviewCartNode
        if (!scheduledInstallationDate) {
          setScheduledInstallationDate(generateScheduledInstallationDate());
        }
        const installDateRaw =
          scheduledInstallationDate || generateScheduledInstallationDate();

        // Use displayDate if available (from self-scheduling), otherwise format it
        let installDate;
        if (installDateRaw && installDateRaw.displayDate) {
          installDate = `Installation on ${installDateRaw.displayDate}`;
        } else {
          installDate = formatInstallationMessage(installDateRaw);
        }

        // Update cart data immediately when reviewCartNode loads
        setTimeout(() => {
          // Calculate selected add-ons
          const hasSelectedAddOns = Object.values(selectedAddOns).some(Boolean);
          const addonPrice = hasSelectedAddOns ? '$3.95' : '$0.00';
          const addonName = hasSelectedAddOns
            ? 'WI-FI Mesh Extender'
            : 'No Add-ons Selected';

          // Calculate totals
          const basePrice = parsePrice(selectedProduct?.price ?? '$0.00');
          const addOnPrice = hasSelectedAddOns ? 3.95 : 0;
          const totalItems = hasSelectedAddOns ? 2 : 1;

          // Calculate discount based on autopay and paperless settings
          let discount = 0;
          if (autoPayValue === 'yes') discount += 5;
          if (paperlessValue === 'yes') discount += 5;

          console.log('💰 Discount calculation:', {
            autoPayValue,
            paperlessValue,
            discount,
          });

          const orderValue = basePrice + addOnPrice - discount;

          // Update cart display
          setValue(
            container,
            ELEMENT_IDS.CHECKOUT_SERVICE_ADDRESS,
            addressInputValue
          );
          setValue(
            container,
            ELEMENT_IDS.CHECKOUT_SHIPPING_ADDRESS,
            addressInputValue
          );
          setValue(
            container,
            ELEMENT_IDS.CHECKOUT_PLAN_NAME,
            formatProductName(selectedProduct)
          );
          setValue(
            container,
            ELEMENT_IDS.CHECKOUT_PLAN_PRICE,
            `${selectedProduct?.price ?? '$0.00'}/month`
          );
          setValue(container, ELEMENT_IDS.CHECKOUT_ADDON_NAME, addonName);
          setValue(
            container,
            ELEMENT_IDS.CHECKOUT_ADDON_PRICE,
            `${addonPrice}/month`
          );
          // Only show installation date for add-ons when they are selected
          if (hasSelectedAddOns) {
            setInstallationDate(
              container,
              ELEMENT_IDS.CHECKOUT_ADDON_INSTALL_DATE,
              installDate
            );
          }

          // Update discount display
          setValue(
            container,
            ELEMENT_IDS.CHECKOUT_DISCOUNT,
            `-$${discount.toFixed(2)}`
          );

          setValue(
            container,
            ELEMENT_IDS.CHECKOUT_ORDER_TOTAL,
            `$${orderValue.toFixed(2)}`
          );
          setValue(
            container,
            ELEMENT_IDS.CHECKOUT_ORDER_VALUE,
            `$${orderValue.toFixed(2)}`
          );

          // Show/hide add-ons section based on selection
          const addonSection = container
            .querySelector(`#${ELEMENT_IDS.CHECKOUT_ADDON_NAME}`)
            ?.closest('.reviewCartNode-SectionHeaderRow');
          if (addonSection) {
            addonSection.style.display = hasSelectedAddOns ? 'flex' : 'none';
          }

          // Update order summary details
          setValue(
            container,
            ELEMENT_IDS.CHECKOUT_PLAN_NAME_SUMMARY,
            formatProductName(selectedProduct)
          );
          setValue(
            container,
            ELEMENT_IDS.CHECKOUT_PLAN_PRICE_SUMMARY,
            selectedProduct?.price ?? '$0.00'
          );
          setValue(
            container,
            ELEMENT_IDS.CHECKOUT_ADDON_NAME_SUMMARY,
            'WI-FI Mesh Extender'
          );
          setValue(
            container,
            ELEMENT_IDS.CHECKOUT_ADDON_PRICE_SUMMARY,
            '$3.95'
          );

          // Show/hide add-on summary item
          const addonSummaryItem = container.querySelector(
            '#addon-summary-item'
          );
          if (addonSummaryItem) {
            addonSummaryItem.style.display = hasSelectedAddOns
              ? 'flex'
              : 'none';
          }

          console.log('✅ ReviewCart updated:', {
            selectedProduct: selectedProduct?.title,
            hasAddOns: hasSelectedAddOns,
            orderValue: orderValue,
            installDate: installDate,
          });
        }, 100);

        const handleRadioChange = () => {
          const selectedAutoPay =
            document.querySelector('input[name="autopay"]:checked')?.value ||
            '';
          const selectedPaperless =
            document.querySelector('input[name="paperless"]:checked')?.value ||
            '';

          setAutoPayValue(selectedAutoPay);
          setPaperlessValue(selectedPaperless);
        };

        const autoPayRadios = document.querySelectorAll(
          'input[name="autopay"]'
        );
        const paperlessRadios = document.querySelectorAll(
          'input[name="paperless"]'
        );
        autoPayRadios.forEach((el) =>
          el.addEventListener('change', handleRadioChange)
        );
        paperlessRadios.forEach((el) =>
          el.addEventListener('change', handleRadioChange)
        );
        // Don't call handleRadioChange() immediately - state already defaults to 'yes'

        const handleTermsChange = () => {
          const checked = document.querySelector(
            '#termsConditionNode-checkbox'
          )?.checked;
          setIsTermsAccepted(!!checked);
        };
        const termsCheckbox = document.querySelector(
          '#termsConditionNode-checkbox'
        );
        if (termsCheckbox) {
          termsCheckbox.addEventListener('change', handleTermsChange);
          handleTermsChange();
        }

        // Note: Proceed button click is handled by useTriggerAction hook (line ~211)
        // which calls handleCheckoutProceedPress() to render TermsConditionNode
        // and load the terms PDF/URL via loadTermsPDF()

        // Cleanup function for the entire useEffect
        return () => {
          autoPayRadios.forEach((el) =>
            el.removeEventListener('change', handleRadioChange)
          );
          paperlessRadios.forEach((el) =>
            el.removeEventListener('change', handleRadioChange)
          );
          if (termsCheckbox) {
            termsCheckbox.removeEventListener('change', handleTermsChange);
          }
        };
      }
    }
  }, [templateHtml, currentStepId, workflow]);

  // ==================== START CONFIRMATION NODE =========================
  useEffect(() => {
    if (containerRef.current && workflow && workflow.nodes) {
      const filteredNode = workflow.nodes.find(
        (node) => node.id === currentStepId
      );

      if (filteredNode?.type === 'orderReceiptNode') {
        const confirmationContainer = containerRef.current;
        const submitOrder = document.getElementById(
          ELEMENT_IDS.NEXT_STEP_BUTTON
        );
        const navigeToSC = document.getElementById(
          ELEMENT_IDS.NAVIGATE_TO_SC
        );
        const navigeToSCLogin = document.getElementById(
          ELEMENT_IDS.NAVIGATE_TO_SC_LOGIN
        );
        
        const updateTextContent = (selector, value) => {
          const el = confirmationContainer.querySelector(selector);
          if (el) el.textContent = value;
        };

        const updateInnerHTML = (selector, value) => {
          const el = confirmationContainer.querySelector(selector);
          if (el) el.innerHTML = value;
        };

        updateTextContent(
          '#order-account-name',
          `${signUpPayload.ecareFirstName} ${signUpPayload.ecareLastName}`
        );
        updateTextContent('#order-phone', `${mobileNumber}`);
        updateTextContent('#order-email', `${signUpPayload.ecareEmailAddress}`);
        updateTextContent('#order-address', `${addressInputValue}`);

        const monthlyRows = confirmationContainer.querySelectorAll(
          '.orderReceiptNode-section:nth-of-type(2) .orderReceiptNode-row:not(.orderReceiptNode-header):not(.orderReceiptNode-totalRow)'
        );

        // Calculate charges based on selected product and add-ons (similar to ReviewCart)
        const hasSelectedAddOns = Object.values(selectedAddOns).some(Boolean);
        const basePrice = parsePrice(selectedProduct?.price ?? '$0.00');
        const addOnPrice = hasSelectedAddOns ? 3.95 : 0;

        const chargeData = [
          {
            description: formatProductName(selectedProduct),
            price: basePrice.toFixed(2),
          },
          {
            description: 'Modem Rental',
            price: '0.00',
          },
          {
            description: 'Estimated Taxes & Fees',
            price: '0.00',
          },
        ];

        // Add WI-FI Mesh Extender if selected
        if (hasSelectedAddOns) {
          chargeData.splice(1, 0, {
            description: 'WI-FI Mesh Extender',
            price: addOnPrice.toFixed(2),
          });
        }

        // Add discount if applicable
        const currentDiscount = calculateDiscount();
        console.log('💰 Order Confirmation Discount:', {
          autoPayValue,
          paperlessValue,
          currentDiscount,
          chargeDataLength: chargeData.length,
          monthlyRowsLength: monthlyRows.length,
        });

        if (currentDiscount > 0) {
          chargeData.push({
            description: 'Discounts',
            price: `-${currentDiscount.toFixed(2)}`,
            isDiscount: true,
          });
        }

        // Update existing rows
        monthlyRows.forEach((row, index) => {
          const columns = row.querySelectorAll('div');
          const charge = chargeData[index];
          if (columns.length >= 2 && charge) {
            columns[0].textContent = charge.description ?? '';
            if (columns.length === 3) {
              columns[1].textContent = '';
              columns[2].textContent = charge.price.startsWith('-')
                ? `-$${charge.price.substring(1)}`
                : `$${charge.price}`;
              // Style discount in green
              if (charge.isDiscount) {
                columns[2].style.color = 'green';
              }
            } else {
              columns[1].textContent = charge.price.startsWith('-')
                ? `-$${charge.price.substring(1)}`
                : `$${charge.price}`;
              // Style discount in green
              if (charge.isDiscount) {
                columns[1].style.color = 'green';
              }
            }
          }
        });

        // If we have more chargeData than existing rows, create new rows for discount
        if (chargeData.length > monthlyRows.length) {
          const monthlySection = confirmationContainer.querySelector(
            '.orderReceiptNode-section:nth-of-type(2) .orderReceiptNode-table'
          );
          const totalRow = monthlySection?.querySelector(
            '.orderReceiptNode-totalRow'
          );

          for (let i = monthlyRows.length; i < chargeData.length; i++) {
            const charge = chargeData[i];
            if (charge && monthlySection && totalRow) {
              // Create new row
              const newRow = document.createElement('div');
              newRow.className = 'orderReceiptNode-row';

              // Create columns
              const col1 = document.createElement('div');
              col1.textContent = charge.description;

              const col2 = document.createElement('div');
              col2.textContent = charge.price.startsWith('-')
                ? `-$${charge.price.substring(1)}`
                : `$${charge.price}`;
              if (charge.isDiscount) {
                col2.style.color = 'green';
              }

              newRow.appendChild(col1);
              newRow.appendChild(col2);

              // Insert before total row
              monthlySection.insertBefore(newRow, totalRow);

              // Add separator
              const separator = document.createElement('hr');
              separator.className = 'orderReceiptNode-separator';
              monthlySection.insertBefore(separator, totalRow);
            }
          }
        }

        // Calculate discount and final total
        const totalDiscount = calculateDiscount();
        const totalPrice = basePrice + addOnPrice - totalDiscount;

        updateTextContent(
          '.orderReceiptNode-totalRow div:last-child',
          `Total: $${totalPrice.toFixed(2)}/mo`
        );

        // Show/hide WI-FI Mesh Extender note based on selection
        const meshExtenderNote = confirmationContainer.querySelector(
          '.orderReceiptNode-notes li:last-child'
        );
        if (meshExtenderNote) {
          meshExtenderNote.style.display = hasSelectedAddOns
            ? 'list-item'
            : 'none';
        }

        const oneTimeRow = confirmationContainer.querySelector(
          '.orderReceiptNode-section:nth-of-type(4) .orderReceiptNode-row'
        );
        if (oneTimeRow) {
          const cols = oneTimeRow.querySelectorAll('div');
          if (cols.length === 3) {
            cols[0].textContent = 'Installation';
            cols[1].textContent = 'Basic Setup';
            cols[2].textContent = '$29.99';
          }
        }

        updateTextContent(
          '.orderReceiptNode-footerRow span',
          signUpPayload.ecareEmailAddress ?? 'no-email@domain.com'
        );

        let paymentLabel =
          autoPayValue === 'yes' ? 'Automatic Payment' : 'Payment Method';

        let paymentDisplayText = '';
        if (autoPayValue === 'yes') {
          if (
            newPaymentDetails.eftCreditCardBrand &&
            newPaymentDetails.eftCreditCardNumber
          ) {
            paymentDisplayText = `${newPaymentDetails.eftCreditCardBrand} ending in ${newPaymentDetails.eftCreditCardNumber}`;
          } else if (newPaymentDetails.eftBankAccountNumber) {
            paymentDisplayText = `Bank Account ending in ${newPaymentDetails.eftBankAccountNumber}`;
          }
        } else {
          if (
            newPaymentDetails.creditCardType &&
            newPaymentDetails.creditCardNumberLast4
          ) {
            paymentDisplayText = `${newPaymentDetails.creditCardType} ending in ${newPaymentDetails.creditCardNumberLast4}`;
          } else if (newPaymentDetails.bankAccountLast4) {
            paymentDisplayText = `Bank Account ending in ${newPaymentDetails.bankAccountLast4}`;
          }
        }

        updateTextContent(
          '.orderReceiptNode-row > div:first-child',
          paymentLabel
        );
        updateTextContent(
          '.orderReceiptNode-creditCard span',
          paymentDisplayText
        );

        updateTextContent('#paperless', addressInputValue);
        updateTextContent('#paperBilling', signUpPayload.ecareEmailAddress);

        [navigeToSC, submitOrder, navigeToSCLogin].forEach(button => {
          button?.addEventListener('click', handleModalNavigation);
        });
      }
    }
  }, [templateHtml]);
  // ==================== END CONFIRMATION NODE =========================

  const handleModalNavigation = (e) => {
  const modal = e.currentTarget.closest('.modal');
  if (modal) {
    modal.style.display = 'none';
  }
  navigateToSignIn();
}
  const handleGetOfferClick = (sa) => {
    if (sa == '') {
      setErrorMessage('Please fill the zipcode');
    } else {
      setErrorMessage('');
    }
    handleNext();
  };

  function getServiceTypeIcon(serviceType) {
    let iconPath = '/images/icons/';
    switch (serviceType) {
      case 'CTV':
        iconPath += 'video.svg';
        break;
      case 'GEN':
        iconPath += 'home-security-icon.svg';
        break;
      case 'ISP':
        iconPath += 'internet.svg';
        break;
      case 'LND':
        iconPath += 'voice.svg';
        break;
      case 'WLS':
        iconPath += 'wireless.svg';
        break;
      default:
        iconPath += 'home-security.svg';
    }
    return iconPath;
  }

  // 2 - NODE : CHOOSEPRODUCT
  useEffect(() => {
    const useAlternate = COMPANY_CONFIG.features?.customNodeHandling;
    //const useAlternate = false;

    if (containerRef.current && workflow && workflow.nodes && !useAlternate) {
      const filteredNode = workflow.nodes.find(
        (node) => node.id === currentStepId
      );
      if ('chooseProductsNode' === filteredNode?.type) {
        if (!selectedService) {
          setValidationFlag(false);
        } else {
          setValidationFlag(true);
        }

        const locationSpan = containerRef.current.querySelector(
          '.location-banner .location'
        );
        const locationChange = containerRef.current.querySelector(
          '.location-banner .change-location'
        );

        if (locationSpan) {
          locationSpan.innerHTML = savedAddress;
          locationChange.addEventListener('click', (event) => {
            const filteredNodeByType = workflow.nodes.find(
              (node) => node.type === 'checkForServiceNode'
            );
            const someValue = filteredNodeByType?.id;
            handleAddressChange(event, someValue);
          });
        }

        const fetchServices = async () => {
          setIsAddressSet(false);
          try {
            const {
              addressLine1,
              addressCityName,
              addressStateAbbr,
              addressPostalCode,
            } = serviceAddress;

            const serviceSpan =
              containerRef.current.querySelector('.serviceAccordion');
            if (serviceSpan) {
              serviceSpan.innerHTML = 'Loading...';
            }

            setIsLoading(true);
            setSelectedService('');
            const payload = {
              serviceAddress: addressLine1,
                  cityName: addressCityName,
                  stateAbbreviation: addressStateAbbr,
                  postalCode: addressPostalCode,
            }
            const response = await axios.post(
              `${process.env.REACT_APP_BASE_URL}/workflows/search/services/`,
              payload,
              {
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `${token}`,
                },
              }
            );
            if (
              response.status === 200 &&
              response.data?.serviceAddresses?.length > 0
            ) {
              const payload = {
                serviceAddressId:
                  response.data.serviceAddresses[0]?.serviceAddressId,
              };
              const getServiceAddress = await axios.post(
                `${process.env.REACT_APP_BASE_URL}/workflows/search/productsByAddressId`,
                payload,
                {
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `${token}`,
                  },
                }
              );
              if (
                getServiceAddress.status === 200 &&
                getServiceAddress.data?.products?.length > 0
              ) {
                const accordionHTML = getServiceAddress.data?.products
                  .filter((s) => s.description !== 'Generic Service')
                  .map((s) => {
                    const isSelected =
                      selectedService?.productId === s.productId;
                    const iconPath = getServiceTypeIcon(s.serviceType);

                    const selClass = isSelected ? 'selected open' : '';
                    return `
        <div class="accordionItem ${selClass}" data-service='${JSON.stringify(
          s
        )}'>
          <div class="accordionHeader">
            <span class="plan-icon">
              <img src="${iconPath}" alt="${s.serviceType}" />
            </span>
            ${s.description} - ${s.serviceTypes[0]?.serviceTypeDescription}
            <span class="accordionIcon">
              <img src="${isSelected ? CARET_UP_SVG : CARET_DOWN_SVG}" alt="caret" />
            </span>
          </div>
          <div class="accordionBody" style="display: ${
            isSelected ? 'block' : 'none'
          };">
            <p>${s.description} - ${s.serviceTypes?.serviceTypeDescription ?? ''}</p>
          </div>
        </div>
      `;
                  })
                  .join('');

                if (serviceSpan) {
                  serviceSpan.innerHTML = accordionHTML;
                }

                const items =
                  serviceSpan?.querySelectorAll('.accordionItem') || [];
                const continueButton =
                  containerRef.current.querySelector('.continue-button');
                if (continueButton) {
                  continueButton.setAttribute('disabled', 'disabled');
                }

                items.forEach((item) => {
                  item.addEventListener('click', async (event) => {
                    const clickedItem = event.currentTarget;
                    const isOpen = clickedItem.classList.contains('open');
                    const serviceData = JSON.parse(
                      clickedItem.getAttribute('data-service')
                    );
                    const allItems =
                      serviceSpan.querySelectorAll('.accordionItem');

                    allItems.forEach((el) => {
                      el.classList.remove('selected', 'open');
                      el.querySelector('.accordionBody').style.display = 'none';
                      const iconImg = el.querySelector('.accordionIcon img');
                      if (iconImg) {
                        iconImg.src = CARET_DOWN_SVG;
                      }
                    });

                    if (!isOpen) {
                      clickedItem.classList.add('selected', 'open');
                      const body = clickedItem.querySelector('.accordionBody');
                      const iconImg =
                        clickedItem.querySelector('.accordionIcon img');
                      if (body) body.style.display = 'block';
                      if (iconImg) {
                        iconImg.src = CARET_UP_SVG;
                      }

                      setSelectedService(serviceData);
                      setValidationFlag(true);

                      body.innerHTML = `<div class="productLoader">Loading plans...</div>`;

                      await fetchAndRenderProducts(serviceData, body);

                      if (continueButton) {
                        continueButton.removeAttribute('disabled');
                      }
                    } else {
                      setSelectedService(null);
                      setValidationFlag(false);

                      if (continueButton) {
                        continueButton.setAttribute('disabled', 'disabled');
                      }
                    }
                  });
                });
              } else {
                if (serviceSpan) {
                  serviceSpan.innerHTML = `
                    <div style="
                      display: flex;
                      flex-direction: column;
                      align-items: center;
                      padding: 40px 20px;
                      text-align: center;
                    ">
                      <div style="
                        width: 80px;
                        height: 80px;
                        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                        border-radius: 50%;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin-bottom: 20px;
                      ">
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="M16 16s-1.5-2-4-2-4 2-4 2"></path>
                          <line x1="9" y1="9" x2="9.01" y2="9"></line>
                          <line x1="15" y1="9" x2="15.01" y2="9"></line>
                        </svg>
                      </div>
                      <h3 style="font-size: 20px; color: #1a202c; margin: 0 0 12px 0;">
                        No Services Available
                      </h3>
                      <p style="font-size: 14px; color: #718096; margin: 0;">
                        We're sorry, but there are currently no services available for this address.
                      </p>
                    </div>
                  `;
                }
              }
            }
          } catch (error) {
            console.error('Error fetching services:', error);
          } finally {
            setTimeout(() => setIsLoading(false), 1000);
          }
        };

        fetchServices();
      }
    } else {
      if (containerRef.current && workflow && workflow.nodes) {
        const filteredNode = workflow.nodes.find(
          (node) => node.id === currentStepId
        );
        if ('chooseProductsNode' === filteredNode.type) {
          console.log(selectedService);
          if (!selectedService) {
            setValidationFlag(false);
          } else {
            setValidationFlag(true);
          }

          const locationSpan = containerRef.current.querySelector(
            '.location-banner .location'
          );
          const locationChange = containerRef.current.querySelector(
            '.location-banner .change-location'
          );

          if (locationSpan) {
            locationSpan.innerHTML = savedAddress;
            locationChange.addEventListener('click', (event) => {
              const filteredNodeByType = workflow.nodes.find(
                (node) => node.type === 'checkForServiceNode'
              );
              const someValue = filteredNodeByType.id; // This is the value you want to pass
              handleAddressChange(event, someValue);
            });
          }

          const fetchServices = async (query) => {
            try {
              let zipCode = serviceAddress?.addressPostalCode.toString();
              const sa = serviceAddress?.addressLine1.toString();
              const city = serviceAddress?.addressCityName.toString();
              const state = serviceAddress?.addressStateAbbr.toString();
              const pc = zipCode.toString();
              const serviceSpan =
                containerRef.current.querySelector('.serviceCards');
              if (serviceSpan) {
                serviceSpan.innerHTML = 'Loading ... ';
              }
              setIsLoading(true);
              const payload = {
                serviceAddress: sa,
                    cityName: city,
                    stateAbbreviation: state,
                    postalCode: pc,
              }
              const response = await axios.post(
                  `${process.env.REACT_APP_BASE_URL}/workflows/search/services/`,
                  payload,
                  {
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `${token}`,
                    },
                  }
                );

              if (response.status === 200) {
                if (response.data && response.data.serviceAddresses) {
                  const services =
                    response.data.serviceAddresses[0].serviceAreas;
                  // Insert the generated HTML into the DOM
                  if (serviceSpan) {
                    serviceSpan.innerHTML = `${generateServiceCards(services)}`;
                    const serviceCards =
                      document.querySelectorAll('.serviceCard');
                    const continueButton =
                      document.querySelector('.continue-button');
                    if (continueButton) {
                      continueButton.setAttribute('disabled', 'disabled');
                    }
                    serviceCards.forEach((card) => {
                      card.addEventListener('click', handleCardClick);
                    });
                  }
                }
                //const updatedTemplateHtml = response.data.template.replace('{{savedAddress}}', savedAddress);
                //setTemplateHtml(DOMPurify.sanitize(updatedTemplateHtml)); // Sanitize HTML before setting it
                // && response.data.deleted_at == null && response.data.template_status === 'publish'
              } else {
                //setTemplateHtml(DOMPurify.sanitize(getNoContentMessage())); // Sanitize fallback HTML
              }
            } catch (error) {
              console.error('Error fetching template HTML:', error);
              //setTemplateHtml(DOMPurify.sanitize(getNoContentMessage())); // Sanitize fallback HTML
            } finally {
              setTimeout(() => {
                setIsLoading(false);
              }, 2000);
              //setIsLoadingTemplate(false);
            }
          };
          fetchServices();
          // select service
          function handleCardClick(event) {
            const clickedCard = event.currentTarget;

            const serviceData = JSON.parse(
              clickedCard.getAttribute('data-service')
            );
            console.log('Service data:', serviceData);

            // Remove 'selected' from all cards
            const allCards = document.querySelectorAll('.serviceCard');
            allCards.forEach((card) => card.classList.remove('selected'));

            // Add 'selected' to the clicked card
            clickedCard.classList.add('selected');

            // Set service and enable navigation
            setSelectedService(serviceData);
            setValidationFlag(true);

            // Moved to next screen.
            //onProductCardClick();
            //renderServiceSelection();

            //handleNext(true);
            const domain = COMPANY_CONFIG.domain;

            if (domain !== 'highline') {
              handleNext(true);
            } else {
              setServiceLoad(true);
            }
          }
        }
      }
    }
  }, [templateHtml, savedAddress]);

  // HIGHLINE CHANGE ONLY FOR CHOOSE PRODUCT AND SERVICE PRODUCTS
  const [selectedService, setSelectedService] = useState('');
  function generateServiceCards(services) {
    return services
      .filter((service) => service.serviceTypeDescription !== 'Generic Service')
      .map((service) => {
        // Map serviceType to icon class
        let iconPath = '/images/icons/';
        switch (service.serviceType) {
          case 'CTV':
            iconPath += 'video.svg';
            break;
          case 'GEN':
            iconPath += 'home-security-icon'; // Generic service icon
            break;
          case 'ISP':
            iconPath += 'internet.svg';
            break;
          case 'LND':
            iconPath += 'voice.svg';
            break;
          case 'WLS':
            iconPath += 'wireless.svg';
            break;
          default:
            iconPath += 'home-security.svg'; // default-icon
        }

        let sel =
          selectedService?.serviceProviderType === service.serviceType
            ? 'selected'
            : '';
        return `
        <div class="serviceCard ${sel}"  data-service='${JSON.stringify(
          service
        )}'>
          <img src="${iconPath}" alt="..." />
              <p>${service.serviceTypeDescription.split(' ')[0]}</p>
        </div>
      `;
      })
      .join('');
  }

  // useEffect(() => {
  //   if (!containerRef.current || !workflow || !workflow.nodes) return;

  //   const filteredNode = workflow.nodes.find(
  //     (node) => node.id === currentStepId
  //   );

  // }, [workflow, currentStepId, token, templateHtml, selectedService]);

  useEffect(() => {
    if (!containerRef.current || !workflow || !workflow.nodes) return;

    const filteredNode = workflow.nodes.find(
      (node) => node.id === currentStepId
    );
    if (filteredNode?.type === 'chooseProductsNode' && serviceLoad) {
      const signUpBtn = containerRef.current.querySelector('.select-plan-btn');
      const handleSignUpClick = () => handleNext();

      if (signUpBtn) {
        signUpBtn.addEventListener('click', handleSignUpClick);
      }
      const serviceId = selectedService.priceAreaId || 8;
      const payload = {
        productId: serviceId,
        includeCharges: 'Y',
      };
      const fetchProducts = async () => {
        try {
          //     let response = await axios.post(
          //   `${process.env.REACT_APP_BASE_URL}/workflows/search/products/`,
          //   payload,
          //   {
          //     headers: {
          //       'Content-Type': 'application/json',
          //       Authorization: `${token}`,
          //     },
          //   }
          // );
          let response = { data: mockProductResponse };
          if (response?.data?.products?.length > 0) {
            const productsAll =
              response.data.products[0]?.serviceTypes?.[0]?.packages?.[0]
                ?.bundles?.[0]?.charges ?? [];

            setProductList(() => {
              if (productsAll.length > 0) {
                renderProductList(productsAll);
              }
              return productsAll;
            });
            // setProductList(productsAll);
            // alert(productsAll.length);
            // if (productsAll.length > 0) {
            //   renderProductList(productsAll);
            //  }
          } else {
            console.warn('No product data available.');
            setProductList([]);
          }
        } catch (error) {
          console.error('Error while fetching product data:', error);
        }
      };

      fetchProducts();

      let productSpan = containerRef.current.querySelector(
        '.serviceSelectionNode-Container'
      );
      if (productSpan) {
        productSpan.innerHTML = 'Loading ...';
      }

      //setIsLoading(true);

      const renderProductList = (products) => {
        let productCards = [];
        let productSpan = document.getElementById('chooseProduct');

        if (productSpan) {
          const planWrapper =
            productSpan.parentElement.querySelector('.planWrapper');
          if (planWrapper) {
            planWrapper.remove();
            console.log('✅ planWrapper removed successfully');
          } else {
            console.warn('⚠️ No planWrapper found to remove');
          }
          productSpan.innerHTML = '';

          // ⬛ Product Content Wrapper
          const productContainer = document.createElement('div');
          productContainer.className = 'products';

          const title = document.createElement('h2');
          title.className = 'serviceSelectionNode-Title';
          title.textContent = 'Prepaid Internet Plans';

          const subtitle = document.createElement('p');
          subtitle.className = 'serviceSelectionNode-Subtitle';
          subtitle.textContent =
            'Explore and choose your services. You can make changes at any time.';

          const grid = document.createElement('div');
          if (isACS) grid.className = 'serviceSelectionNode-Grid acs';
          else grid.className = 'serviceSelectionNode-Grid';
          //  grid.className = 'serviceSelectionNode-Grid';
          grid.innerHTML = productListing(products);

          // ⬛ Back Button Wrapper
          const backBtnWrapper = document.createElement('div');
          backBtnWrapper.className = 'serviceSelectionNode-buttonRow';
          backBtnWrapper.innerHTML = `
            <button 
              id="previous-step-button"
              data-element-id="previous-step-button"
              type="button"
              class="default-button button_button__punpJ button_secondary__f6I2S"
            >
              <span class="button_content__a8Pcg">
                <span>Back</span>
              </span>
            </button>
          `;

          // ⬛ Append in correct order: content → grid → backBtn
          productContainer.appendChild(title);
          productContainer.appendChild(subtitle);
          productContainer.appendChild(grid);

          // ⬛ Clear and add everything to productSpan
          productSpan.appendChild(productContainer);
          productSpan.appendChild(backBtnWrapper);
          // Add listeners to individual plan cards
          productCards = [
            ...productContainer.querySelectorAll(
              '.serviceSelectionNode-Card #next-step-button'
            ),
          ];
          productCards.forEach((card) => {
            card.addEventListener('click', handleProductCardClick);
          });
        }

        setTimeout(() => {
          //setIsLoading(false);
        }, 2000);

        return () => {
          if (signUpBtn) {
            signUpBtn.removeEventListener('click', handleSignUpClick);
          }
          productCards.forEach((card) => {
            card.removeEventListener('click', handleProductCardClick);
          });
        };
      };
    }
  }, [workflow, currentStepId, token, templateHtml, selectedService]);

  // function productListing(products) {
  //   return products
  //     .slice(0, 20)
  //     .map((product, index) => {
  //       if (product?.priceAreas[0]?.chargePrice == 0) return;
  //       return `
  //             <div key='${index}' class="serviceSelectionNode-Card ">
  //  <div class="serviceSelectionNode-TopSection">
  //     <img src="/images/icons/cloud-icon.png" alt="cloud" class="serviceSelectionNode-CloudImg"/>
  //     <div class="serviceSelectionNode-Speed">50 Mbps</div>
  //     <div class="serviceSelectionNode-Price"><span class="serviceSelectionNode-PriceBold">$${
  //       product?.priceAreas[0]?.chargePrice || 'N/A'
  //     }</span><span class="serviceSelectionNode-PriceUnit">/month</span></div>
  //  </div>
  //  <div class="serviceSelectionNode-BottomSection">
  //     <ul class="serviceSelectionNode-Details">
  //        <li>
  //           <svg stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
  //              <polyline points="20 6 9 17 4 12"></polyline>
  //           </svg>
  //           ${product.chargeTypeDescription}
  //        </li>
  //        <li>
  //           <svg stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" stroke-linecap="round" stroke-linejoin="round" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg">
  //              <polyline points="20 6 9 17 4 12"></polyline>
  //           </svg>
  //           ${product?.chargeDescription || 'N/A'}
  //        </li>
  //     </ul>
  //     <button id="next-step-button" data-element-id="next-step-button" type="button" class="serviceSelectionNode-button button_button__fQmBi button_primary__gMyAO button_fullWidth__eW2MG" fdprocessedid="rkvjop"><span class="button_content__7VtGF button_withSides__eQAzO"><span>Select Plan</span><span class="button_side__mo2l0"><span class="serviceSelectionNode-Arrow">→</span></span></span></button>
  //  </div>
  //  </div>

  //           `;
  //     })
  //     .join('');
  // }

  function handleProductCardClick(event) {
    const clickedButton = event.currentTarget;
    const productCard = clickedButton.closest('.serviceSelectionNode-Card');
    if (!productCard) return;

    const allProductCards = document.querySelectorAll(
      '.serviceSelectionNode-Card'
    );
    allProductCards.forEach((card) => card.classList.remove('selected'));
    productCard.classList.add('selected');

    const detailItems = productCard.querySelectorAll(
      '.serviceSelectionNode-Details li'
    );

    const productDetails = {
      title: productCard.querySelector('h3')?.textContent.trim() ?? '', // Only works if <h3> exists
      price:
        productCard
          .querySelector('.serviceSelectionNode-PriceBold')
          ?.textContent.trim() ?? '', // Only if .price exists
      typeDescription:
        detailItems[0]?.textContent.trim().replace(/\s+\/\/.*$/, '') ?? '',
      description:
        detailItems[1]?.textContent.trim().replace(/\s+\/\/.*$/, '') ?? '',
    };

    console.log('Selected Product:', productDetails);
    setSelectedProduct(productDetails);
  }
  //==============================================================
  const fetchAndRenderProducts = async (serviceData, container) => {
    setExpandedProduct(serviceData);
    const productId = serviceData.productId || 8;
    const payload = {
      productId: productId,
      includeCharges: 'Y',
    };

    const isACS =
      COMPANY_CONFIG.name?.toLowerCase() === 'alaska communications' ||
      COMPANY_CONFIG.domain?.toLowerCase().includes('acs');
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/workflows/search/products/`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${token}`,
          },
        }
      );

      const products =
        response?.data?.products?.[0]?.serviceTypes?.[0]?.packages?.[0]
          ?.bundles?.[0]?.charges ?? [];
      console.log(products, 'products in mutistep form ');
      container.innerHTML = '';

      if (products.length > 0) {
        const productContainer = document.createElement('div');
        productContainer.className = 'products';

        const grid = document.createElement('div');
        if (isACS) grid.className = 'serviceSelectionNode-Grid acs';
        else grid.className = 'serviceSelectionNode-Grid';
        grid.innerHTML = productListing(products).replaceAll(
          'id="next-step-button"',
          'class="next-step-button"'
        );

        const backBtnWrapper = document.createElement('div');
        backBtnWrapper.className = 'accordionBackBtnWrapper';
        backBtnWrapper.innerHTML = `
          <button class="default-button back-button">Back</button>
        `;

        productContainer.appendChild(grid);
        productContainer.appendChild(backBtnWrapper);
        container.appendChild(productContainer);

        const productButtons = productContainer.querySelectorAll(
          '.serviceSelectionNode-Card .next-step-button'
        );

        productButtons.forEach((btn) => {
          btn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            handleProductCardClick(e);
            handleNext(true);
          });
        });

        const broadbandToggles = productContainer.querySelectorAll(
          '.serviceSelectionNode-Card .broadband-label-toggle'
        );

        broadbandToggles.forEach((toggleBtn) => {
          const card = toggleBtn.closest('.serviceSelectionNode-Card');
          const accordionContent = card.querySelector(
            '.broadband-label-content'
          );

          accordionContent.style.display = 'block';
          toggleBtn.innerHTML = 'Broadband Label ▲';

          toggleBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();

            const isOpen = accordionContent.style.display === 'block';
            accordionContent.style.display = isOpen ? 'none' : 'block';
            toggleBtn.innerHTML = isOpen
              ? 'Broadband Label ▼'
              : 'Broadband Label ▲';
          });
        });

        backBtnWrapper
          .querySelector('.back-button')
          .addEventListener('click', () => {
            container.innerHTML = '';
          });
      } else {
        container.innerHTML = `
          <div style="
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 60px 20px;
            text-align: center;
            min-height: 400px;
          ">
            <div style="
              width: 120px;
              height: 120px;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              margin-bottom: 30px;
              box-shadow: 0 10px 30px rgba(102, 126, 234, 0.3);
            ">
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M16 16s-1.5-2-4-2-4 2-4 2"></path>
                <line x1="9" y1="9" x2="9.01" y2="9"></line>
                <line x1="15" y1="9" x2="15.01" y2="9"></line>
              </svg>
            </div>
            
            <h2 style="
              font-size: 28px;
              font-weight: 700;
              color: #1a202c;
              margin: 0 0 16px 0;
            ">
              No Products Available
            </h2>
            
            <p style="
              font-size: 16px;
              color: #718096;
              max-width: 500px;
              margin: 0 0 40px 0;
              line-height: 1.6;
            ">
              We're sorry, but there are currently no products available for this service. 
              Please check back later or contact our support team for assistance.
            </p>
            
            <div style="display: flex; gap: 16px; flex-wrap: wrap; justify-content: center;">
              <button 
                id="no-products-back-btn"
                style="
                  padding: 14px 32px;
                  font-size: 16px;
                  font-weight: 600;
                  color: white;
                  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                  border: none;
                  border-radius: 8px;
                  cursor: pointer;
                  transition: all 0.3s ease;
                  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
                "
                onmouseover="this.style.transform='translateY(-2px)'; this.style.boxShadow='0 6px 20px rgba(102, 126, 234, 0.5)';"
                onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 15px rgba(102, 126, 234, 0.4)';"
              >
                Go Back
              </button>
              
              <button 
                id="no-products-exit-btn"
                style="
                  padding: 14px 32px;
                  font-size: 16px;
                  font-weight: 600;
                  color: #667eea;
                  background: white;
                  border: 2px solid #667eea;
                  border-radius: 8px;
                  cursor: pointer;
                  transition: all 0.3s ease;
                "
                onmouseover="this.style.background='#f7fafc'; this.style.transform='translateY(-2px)';"
                onmouseout="this.style.background='white'; this.style.transform='translateY(0)';"
              >
                Exit
              </button>
            </div>
          </div>
        `;

        const backBtn = container.querySelector('#no-products-back-btn');
        const exitBtn = container.querySelector('#no-products-exit-btn');

        if (backBtn) {
          backBtn.addEventListener('click', () => {
            container.innerHTML = '';
          });
        }

        if (exitBtn) {
          exitBtn.addEventListener('click', () => {
            navigate(`/workflow/${workflowId}`, { state: { workflowId } });
          });
        }
      }
    } catch (error) {
      console.error('Error fetching product data:', error);
      container.innerHTML = `<p>Error loading plans. Please try again.</p>`;
    }
  };
  function productListing(products) {
    const isACS =
      COMPANY_CONFIG.name?.toLowerCase() === 'alaska communications' ||
      COMPANY_CONFIG.domain?.toLowerCase().includes('acs');

    // ✅ FOR ACS: Override products with 100% static data (NO API dependency)
    if (isACS) {
      console.log(
        '📌 ACS detected - Using 100% static products (NO API call needed)'
      );
      products = [
        {
          chargeDescription: 'Unlimited Internet 10Mbps',
          chargeTypeDescription: 'High-Speed DSL Internet',
          priceAreas: [{ chargePrice: '$84.99' }],
          downloadSpeed: 'Up to 10 Mbps',
          uploadSpeed: '1 Mbps',
          latency: '10 ms',
          contractLength: '2 Year(s)',
          features: [
            'Includes Premium Home WiFi & Mesh network add-ons available',
            'HomePass™ membership WiFi network management subscription',
            'Flexible month-to-month plan available',
            'Price lock for 2 years (with terms agreement)',
            'TERMS AND CONDITIONS APPLY',
          ],
        },
        {
          chargeDescription: 'Unlimited Internet 15Mbps',
          chargeTypeDescription: 'High-Speed DSL Internet',
          priceAreas: [{ chargePrice: '$89.99' }],
          downloadSpeed: 'Up to 15 Mbps',
          uploadSpeed: '1 Mbps',
          latency: '10 ms',
          contractLength: '2 Year(s)',
          features: [
            'Includes Premium Home WiFi & Mesh network add-ons available',
            'HomePass™ membership WiFi network management subscription',
            'Flexible month-to-month plan available',
            'Price lock for 2 years (with terms agreement)',
            'TERMS AND CONDITIONS APPLY',
          ],
        },
      ];
    }

    if (COMPANY_CONFIG.domain === 'archtop') {
      products = [
        {
          chargeDescription: 'Archtop Fiber 500/500',
          chargeTypeDescription: 'Internet Service.',
          priceAreas: [{ chargePrice: '$69.99' }],
        },
        {
          chargeDescription: 'Archtop Fiber 1GB/1GB',
          chargeTypeDescription: 'Internet Service.',
          priceAreas: [{ chargePrice: '$79.99' }],
        },
        {
          chargeDescription: 'Archtop Fiber 2GB/2GB',
          chargeTypeDescription: 'Internet Service.',
          priceAreas: [{ chargePrice: '$99.99' }],
        },
      ];
    }
    return products
      .slice(0, 20)
      .map((product, index) => {
        if (product?.priceAreas[0]?.chargePrice[0] == 0) return;

        let monthlyPrice = product?.priceAreas[0]?.chargePrice || 'N/A';
        // Ensure dollar sign is present
        if (monthlyPrice !== 'N/A' && !monthlyPrice.toString().includes('$')) {
          monthlyPrice = `$${monthlyPrice}`;
        }
        const contractLength = product?.contractLength || 'N/A';
        const downloadSpeed = product?.downloadSpeed || 'N/A';
        const uploadSpeed = product?.uploadSpeed || 'N/A';
        const latency = product?.latency || 'N/A';
        const supportNumber =
          product?.supportNumber || COMPANY_CONFIG.support.phoneNumber;
        const supportUrl = product?.supportUrl || COMPANY_CONFIG.support.url;

        const isInterBel =
          COMPANY_CONFIG.name?.toLowerCase() === 'interbel' ||
          COMPANY_CONFIG.fullName?.toLowerCase().includes('interbel') ||
          COMPANY_CONFIG.accountOrgName?.toLowerCase() === 'interbel' ||
          COMPANY_CONFIG.domain?.toLowerCase() === 'interbel';

        // InterBel pricing override
        if (isInterBel) {
          const planName = product?.chargeDescription?.toLowerCase() || '';
          if (planName.includes('100')) monthlyPrice = '$85.00';
          else if (planName.includes('500')) monthlyPrice = '$105.00';
          else if (planName.includes('gig') || planName.includes('1000'))
            monthlyPrice = '$125.00';
        }

        // Smart Label ID mapping function (company-specific)
        const getLabelId = (chargeDescription) => {
          if (!chargeDescription) return 'default-label-id';

          // Try to match "X/Y" format first (e.g., "100/100", "1GB/1GB")
          let speedMatch = chargeDescription.match(
            /(\d+(?:GB|Mbps)?\/\d+(?:GB|Mbps)?)/i
          );
          let speed = speedMatch ? speedMatch[1] : null;

          // If no match, try single speed format (e.g., "10Mbps", "15Mbps")
          if (!speed) {
            speedMatch = chargeDescription.match(/(\d+)\s*Mbps/i);
            if (speedMatch) {
              // For ACS single-speed format, assume symmetric (10Mbps = 10/10)
              const singleSpeed = speedMatch[1];
              speed = `${singleSpeed}/${singleSpeed}`;
            }
          }

          if (!speed) return 'default-label-id';

          const normalizeSpeed = (speedStr) => {
            if (speedStr === '1000/1000') return '1GB/1GB';
            return speedStr.replace(/Mbps/gi, '');
          };
          const normalizedSpeed = normalizeSpeed(speed);
          let speedToLabelMap;

          if (isACS) {
            speedToLabelMap = {
              '10/10': 'xoPd4TOiFMZP',
              '15/15': 'aJIy5CoycImb',
            };
          } else if (isArchtop) {
            speedToLabelMap = {
              '500/500': '2GyMfEN9fpbg',
              '1GB/1GB': '0IKbsjkKop0z',
              '2GB/2GB': 't7edpe51wnPu',
            };
          } else {
            speedToLabelMap = {
              '100/100': 'tj1XPdUjsyav',
              '500/500': 'B0mVcqALri1G',
              '1GB/1GB': 'cfDRhfK0m5hl',
            };
          }

          return (
            speedToLabelMap[normalizedSpeed] ||
            product?.broadbandLabelId ||
            product?.labelId ||
            'default-label-id'
          );
        };

        const broadbandLabelId = getLabelId(product.chargeDescription);

        // Parse charge description
        const charge_description = product.chargeDescription;
        const words = charge_description.split(' ');
        const product_plan = words.pop();
        const product_desc = words.join(' ');

        // ==================== INTERBEL STATIC LABEL ====================
        const getStaticLabelHtml = (plan) => {
          let planName = plan?.chargeDescription?.toLowerCase() || '';
          let labelData = {};

          if (planName.includes('100')) {
            labelData = {
              plan: 'Fiber 100 - CLEC-South',
              price: '$85.00',
              down: '93 Mbps',
              up: '93 Mbps',
              latency: '11 ms',
              id: 'F0006301592CLECSOUFIBER100',
            };
          } else if (planName.includes('500')) {
            labelData = {
              plan: 'Fiber 500 - CLEC-South',
              price: '$105.00',
              down: '450 Mbps',
              up: '450 Mbps',
              latency: '11 ms',
              id: 'F0006301592CLECSOUFIBER500',
            };
          } else if (planName.includes('gig') || planName.includes('1000')) {
            labelData = {
              plan: 'Fiber Gig - CLEC-South',
              price: '$125.00',
              down: '925 Gbps',
              up: '925 Gbps',
              latency: '11 ms',
              id: 'F0006301592CLECSOUFIBERGIG',
            };
          } else {
            labelData = {
              plan: plan?.chargeDescription || 'InterBel Broadband',
              price: `$${monthlyPrice}`,
              down: `${downloadSpeed}`,
              up: `${uploadSpeed}`,
              latency: `${latency} ms`,
              id: 'INTERBELDEFAULT',
            };
          }

          if (isInterBel) monthlyPrice = labelData.price;

          return `
          <div class="cf_bcl_wc" style="font-family:'Libre Franklin',sans-serif;border:4px solid #231F20;background:#fff;padding:16px;color:#231F20;max-width:500px;margin:10px auto;">
            <div style="border-bottom:8px solid #231F20;padding-bottom:6px;margin-bottom:10px;">
              <h2 style="margin:0 0 5px 0;font-weight:900;font-size:30px;border-bottom:2px solid #231F20;">Broadband Facts</h2>
              <p style="margin:0;font-weight:700;">InterBel Telephone Cooperative, Inc.</p>
              <p style="margin:0;font-weight:900;">${labelData.plan}</p>
              <p style="margin:0;font-size:13px;">Fixed Broadband Consumer Disclosure</p>
            </div>
            
            <div style="border-bottom:3px solid #231F20;padding:5px 0;">
              <h4 style="margin:0 0 5px 0;font-weight:700;border-bottom:2px solid #231F20;padding-bottom:3px;">Monthly Price</h4>
              <p style="display:flex;justify-content:space-between;margin:0;"><span>Base Price</span><strong>${labelData.price}</strong></p>
              <p style="display:flex;justify-content:space-between;margin:0;"><span>Introductory Rate</span><span>No</span></p>
            </div>

            <div style="border-bottom:3px solid #231F20;padding:5px 0;">
              <h4 style="margin:0 0 5px 0;font-weight:700;">Additional Charges & Terms</h4>
              <p style="margin:0 0 8px 0;font-weight:600;">One-Time Fees at the Time of Purchase</p>
              <p style="display:flex;justify-content:space-between;margin:0;"><span>Router</span><span><strong>$75.00</strong></span></p>
              <p style="display:flex;justify-content:space-between;margin:0;"><span>Connect Fee</span><span><strong>$20.00</strong></span></p>
              <p style="display:flex;justify-content:space-between;margin:0 0 8px 0;"><span>Government Taxes</span><span><strong>Varies by location</strong></span></p>
            </div>

            <div style="border-bottom:3px solid #231F20;padding:5px 0;">
              <h4 style="margin:0 0 5px 0;font-weight:700;">Discounts & Bundles</h4>
              <p style="margin:0;font-size:13px;line-height:1.4;word-wrap:break-word;overflow-wrap:break-word;white-space:normal;max-width:100%;text-align:left;">
Visit the link below for available billing discounts and pricing options for broadband service bundled with other services like video, phone, and wireless service, and use of your own equipment.</p>
            </div>

            <div style="border-bottom:3px solid #231F20;padding:5px 0;">
              <h4 style="margin:0 0 5px 0;font-weight:700;">Speeds Provided with Plan</h4>
              <p style="display:flex;justify-content:space-between;margin:0;"><span>Typical Download Speed</span><span><strong>${labelData.down}</strong></span></p>
              <p style="display:flex;justify-content:space-between;margin:0;"><span>Typical Upload Speed</span><span><strong>${labelData.up}</strong></span></p>
              <p style="display:flex;justify-content:space-between;margin:0;"><span>Typical Latency</span><span><strong>${labelData.latency}</strong></span></p>
            </div>

            <div style="border-bottom:3px solid #231F20;padding:5px 0;">
              <h4 style="margin:0 0 5px 0;font-weight:700;">Data Included with Monthly Price</h4>
              <p style="display:flex;justify-content:space-between;margin:0;"><span>Monthly Data</span><span><strong>Unlimited</strong></span></p>
            </div>

            <div style="border-bottom:3px solid #231F20;padding:5px 0;">
              <h4 style="margin:0 0 5px 0;font-weight:700;">Network Management</h4>
              <p style="margin:0;font-size:13px;">Network Practices</p>
            </div>

            <div style="border-bottom:3px solid #231F20;padding:5px 0;">
              <h4 style="margin:0 0 5px 0;font-weight:700; text-align:left;">Privacy</h4>
             <p style="margin:0; text-align:left;"> <a href="https://interbel.com/privacy-policy/" target="_blank" style="font-weight:bold;color:var(--primary-color);">Privacy Policy</a></p>
            </div>

            <div style="border-bottom:3px solid #231F20;padding:5px 0;">
              <h4 style="margin:0 0 5px 0;font-weight:700;">Customer Support</h4>
              <p style="margin:0;">Phone: <strong>406-889-2311</strong></p>
              <p style="margin:0;">Email: <a href="mailto:getintouch@interbel.com" style="font-weight:bold;color:var(--primary-color);">getintouch@interbel.com</a></p>
              <p style="margin:0;">Website: <a href="https://interbel.com/" target="_blank" style="font-weight:bold;color:var(--primary-color);">https://interbel.com/</a></p>
              <p style="margin:0;">Address: <strong>300 Dewey Ave, Eureka, MT 59917</strong></p>
            </div>

            <div style="padding-top:5px;">
              <p style="margin:0;font-size:12px;word-wrap:break-word;overflow-wrap:break-word;white-space:normal;max-width:100%;text-align:left;">Learn more about the terms used on this label by visiting the Federal Communications Commission's Consumer Resource Center.</p>
              <p style="margin:0;font-size:14px;"><a href="https://fcc.gov/consumer" target="_blank" style="color:var(--primary-color);text-align:right;">fcc.gov/consumer</a></p>
              <p style="margin:0;font-size:14px;margin-top:8px;">Unique Plan Identifier:</p>
              <p style="margin:0;font-size:14px;font-weight:700;">${labelData.id}</p>
            </div>
          </div>
        `;
        };

        // ==================== CWF IMAGE-BASED LABEL ====================
        const getCWFImageLabelHtml = (plan) => {
          let planName = plan?.chargeDescription?.toLowerCase() || '';
          let imageUrl = '';

          if (planName.includes('2 gig') || planName.includes('2gig')) {
            imageUrl =
              'https://cdn-ilabbjp.nitrocdn.com/XvBHNOBiWRKiZrbRCUDkgGekGeKbQVVl/assets/images/optimized/rev-576b40a/www.clearwavefiber.com/wp-content/uploads/2025/09/F00076902580069FIBER2GIGB-Eff.-10.9.25-scaled.webp';
          } else if (
            planName.includes('1 gig') ||
            planName.includes('1gig') ||
            planName.includes('gig')
          ) {
            imageUrl =
              'https://cdn-ilabbjp.nitrocdn.com/XvBHNOBiWRKiZrbRCUDkgGekGeKbQVVl/assets/images/optimized/rev-576b40a/www.clearwavefiber.com/wp-content/uploads/2025/09/F00076902580069FIBER2GIGB-Eff.-10.9.25-scaled.webp';
          } else if (planName.includes('500')) {
            imageUrl =
              'https://cdn-ilabbjp.nitrocdn.com/XvBHNOBiWRKiZrbRCUDkgGekGeKbQVVl/assets/images/optimized/rev-576b40a/www.clearwavefiber.com/wp-content/uploads/2025/09/F00076902580069FIBER2GIGB-Eff.-10.9.25-scaled.webp';
          } else {
            imageUrl =
              'https://cdn-ilabbjp.nitrocdn.com/XvBHNOBiWRKiZrbRCUDkgGekGeKbQVVl/assets/images/optimized/rev-576b40a/www.clearwavefiber.com/wp-content/uploads/2025/09/F00076902580069FIBER2GIGB-Eff.-10.9.25-scaled.webp';
          }

          const iframeHtml = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width,initial-scale=1" />
  <title>Broadband Facts</title>
</head>
<body style="margin:0;padding:0;">
  <img src="${imageUrl}" style="display:block;max-width:100%;height:auto;" alt="CWF Broadband Facts" />
</body>
</html>
`;

          return `
<iframe
  id="broadband-frame"
  scrolling="no"
  srcdoc='${iframeHtml.replace(/'/g, '&apos;')}'
  style="width:100%;min-height:825px;border:none;border-radius:8px;"
  loading="lazy">
</iframe>`;
        };

        // ==================== CHOOSE LABEL TYPE ====================
        const labelContentHtml = isInterBel
          ? getStaticLabelHtml(product)
          : isCWF
            ? getCWFImageLabelHtml(product)
            : `
          <iframe
            id="broadband-frame"
            scrolling="no"
            srcDoc="\`
              <!DOCTYPE html>
              <html lang='en'>
              <head>
                <meta charset='UTF-8'>
                <meta name='viewport' content='width=device-width, initial-scale=1.0'>
                <title>Broadband Label</title>
                <link rel='stylesheet' href='https://cdn.crowdfiber.io/broadband-consumer-labels/widget_label.css' type='text/css'>
                <script defer src='https://cdn.crowdfiber.io/broadband-consumer-labels/widget.js'></script>
              </head>
              <style>
                body {
                  margin: 0;
                  padding: 10px;
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  min-height: 100vh;
                  background: #f8f9fa;
                }
                .cf_bcl_wc {
                  width: 100%;
                  max-width: 500px;
                }
                .bcl_widget-container {
                   padding: 5px 10px;  
                }
                .bcl_link {
      color: ${COMPANY_CONFIG.name === 'ACS' ? '#00a0b0' : null};
}
              </style>
              <body>
                <div class='cf_bcl_wc' data-label-id='${broadbandLabelId}'></div>
              </body>
              </html>
            \`"
            style="width: 100%; height:1000px; border: none; border-radius: 8px;"
            sandbox="allow-scripts allow-same-origin allow-popups"
            loading="lazy">
          </iframe>
        `;

        // ==================== RENDER PRODUCT CARD ====================
        return `
        <div key='${index}' class="serviceSelectionNode-Card">
          <div class="serviceSelectionNode-TopSection">
            <img src="/images/icons/cloud-icon.png" alt="cloud" class="serviceSelectionNode-CloudImg"/>
            <div class="serviceSelectionNode-Speed">${product.chargeDescription}</div>
            <div class="serviceSelectionNode-Price">
              <span class="serviceSelectionNode-PriceBold">${monthlyPrice}</span>
              <span class="serviceSelectionNode-PriceUnit">/month</span>
            </div>
          </div>

          <div class="serviceSelectionNode-BottomSection">
            <ul class="serviceSelectionNode-Details">
              <li>${product.chargeTypeDescription}</li>
              ${product.features ? product.features.map((feature) => `<li>${feature}</li>`).join('') : ''}
              <li>${isACS || COMPANY_CONFIG.domain === 'archtop' || COMPANY_CONFIG.domain === 'cwf'|| COMPANY_CONFIG.domain === 'prtc' ? '' : formatSpeed(product_plan) || 'N/A'}</li>
            </ul>

            <button id="next-step-button" data-element-id="next-step-button"
              class="serviceSelectionNode-Button"
              style="background-color:var(--primary-button-color, var(--primary-color)); color:white; padding:12px 16px; border:none; border-radius:9999px; width:100%;">
              Select Plan <span class="serviceSelectionNode-Arrow">→</span>
            </button>
          </div>

        </div>
      `;
      })
      .filter(Boolean)
      .join('');
  }
  function handleProductCardClick(event) {
    const clickedButton = event.currentTarget;
    const productCard = clickedButton.closest('.serviceSelectionNode-Card');
    if (!productCard) return;

    const allProductCards = document.querySelectorAll(
      '.serviceSelectionNode-Card'
    );
    allProductCards.forEach((card) => card.classList.remove('selected'));
    productCard.classList.add('selected');

    const detailItems = productCard.querySelectorAll(
      '.serviceSelectionNode-Details li'
    );
    const productDetails = {
      title:
        productCard
          .querySelector('.serviceSelectionNode-Speed')
          ?.textContent.trim() ?? '',
      price:
        productCard
          .querySelector('.serviceSelectionNode-PriceBold')
          ?.textContent.trim() ?? '',
      typeDescription: detailItems[0]?.textContent.trim() ?? '',
      description: detailItems[1]?.textContent.trim() ?? '',
    };

    setSelectedProduct(productDetails);
  }

  function formatSpeed(speedStr) {
    if (!speedStr) return '';

    const [download, upload] = speedStr.split('/');

    const unitDownload = download.toUpperCase().includes('GB') ? 'GB' : 'Mbps';
    const unitUpload = upload.toUpperCase().includes('GB') ? 'GB' : 'Mbps';

    return (
      `Download: ${download.replace(/GB/i, '').trim()} ${unitDownload}<br/>` +
      `Upload: ${upload.replace(/GB/i, '').trim()} ${unitUpload}`
    );
  }

  // 1 - CHECKFORSERVICE NODE
  // ====================== START ADDRESS NODE ======================
  useEffect(() => {
    if (!containerRef.current || !workflow?.nodes) return;

    const filteredNode = workflow.nodes.find(
      (node) => node.id === currentStepId
    );

    if (filteredNode?.type !== 'checkForServiceNode') return;

    setValidationFlag(savedAddress.length > 10);

    const capitalizeFirstLetter = (str) =>
      str.charAt(0).toUpperCase() + str.slice(1);
    const signInLink = document.querySelector(
      '.checkForServiceNode-signInText a'
    );

    if (signInLink) {
      signInLink.addEventListener('click', function (e) {
        e.preventDefault();
        navigateToSignIn();
      });
    }

    // Handle card click with company-specific icons
    const handleCardClick = (event) => {
      const clickedCard = event.currentTarget;
      const allCards = containerRef.current?.querySelectorAll(
        '.checkForServiceNode-card'
      );

      if (!allCards) return;

      // Get company-specific icons
      const icons = COMPANY_CONFIG?.ui?.addressForm?.icons || {
        residential: '/images/icons/Residential.png',
        residentialActive: '/images/icons/Residential-active.png',
        business: '/images/icons/Business.png',
        businessActive: '/images/icons/Business-active.png',
      };

      // Remove active state from all cards and set inactive icons
      allCards.forEach((card) => {
        card.classList.remove('active');
        const img = card.querySelector('img');
        const type = card.querySelector('h4')?.innerText.toLowerCase();

        if (img && type) {
          // Set inactive icon based on company config
          img.src =
            icons[type] || `/images/icons/${capitalizeFirstLetter(type)}.png`;
        }
      });

      // Add active state to clicked card and set active icon
      clickedCard.classList.add('active');
      const img = clickedCard.querySelector('img');
      const type = clickedCard.querySelector('h4')?.innerText.toLowerCase();

      if (img && type) {
        // Set active icon based on company config
        const activeKey = `${type}Active`;
        img.src =
          icons[activeKey] ||
          `/images/icons/${capitalizeFirstLetter(type)}-active.png`;
      }

      const accountTypeValue = capitalizeFirstLetter(type);
      setAccountType(accountTypeValue);
      setSubscriberType(accountTypeValue === 'Residential' ? 'R' : 'B');
    };

    const cards = containerRef.current.querySelectorAll(
      '.checkForServiceNode-card'
    );

    cards.forEach((card) => card.addEventListener('click', handleCardClick));

    const input = containerRef.current.querySelector('input[class*="address"]');
    const getOfferBtn = containerRef.current.querySelector(
      '.pre-register-button'
    );
    if (!input) return;

    input.value = savedAddress;
    input.addEventListener('input', handleAddressInputChange);
    const parent = input.parentNode;
    const alertEl =
      parent.querySelector('.address-alert') ||
      (() => {
        const el = document.createElement('div');
        el.className = 'address-alert';
        input.insertAdjacentElement('afterend', el);
        return el;
      })();

    const successEl =
      parent.querySelector('.address-success-message') ||
      (() => {
        const el = document.createElement('div');
        el.className = 'address-success-message';
        parent.appendChild(el);
        return el;
      })();
    const errorEl =
      parent.querySelector('.error-message') ||
      (() => {
        const el = document.createElement('div');
        el.className = 'error-message';
        parent.appendChild(el);
        return el;
      })();

    const updateDropdownPosition = () => {
      const rect = input.getBoundingClientRect();
      const style = {
        position: 'fixed',
        top: `${rect.bottom}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
      };
      setDropdownStyle(style);
      Object.entries(style).forEach(([k, v]) => {
        alertEl.style[k] = v;
      });
    };

    updateDropdownPosition();

    if (savedAddress.replace(/\s+/g, '').length > 3) {
      errorEl.style.display = 'none';
      if (addressAlert === 'No address available' && !isAddressAvailable) {
        errorEl.style.display = 'block'; // Reset display to show the message
        errorEl.innerHTML = `<div class='coming-soon'>
            <h5>Coming soon to your area!</h5>
            <p>Great news! We're expanding our services and will be reaching your area soon. Stay tuned for the updates, and be the first know when we go live. Thank you for your interest-fast, reliable service is on it's way!.</p>
            <div class="actions">
                <button class="get-notified-btn" id='get-notified'>
                    Get notified
                </button>
                <button class='skip-btn' id='clear-address'>
                    Skip for now
                </button>
            </div>
        </div>`;

        const formContainer = containerRef.current.querySelector(
          '.checkForServiceNode-formContainer'
        );
        const bottomActions = containerRef.current.querySelector(
          '.checkForServiceNode-bottomActions'
        );
        const signInText = containerRef.current.querySelector(
          '.checkForServiceNode-signInText'
        );

        if (formContainer) formContainer.style.display = 'flex';
        if (bottomActions) bottomActions.style.display = 'flex';
        if (signInText) signInText.style.display = 'block';

        const getNotified = errorEl?.querySelector('#get-notified');
        const skipButton = errorEl?.querySelector('#clear-address');
        if (getNotified) {
          getNotified.addEventListener('click', () => {
            console.log('Get Notified clicked');
            handleGetNotified();
          });
        }
        if (skipButton) {
          skipButton.addEventListener('click', () => {
            skipInvalidAddress();
          });
        }
      } else {
        //errorEl.style.display = 'block';
        //errorEl.style.display = 'block';
        // errorEl.style.color = 'red';
        // errorEl.style.padding = '5px';
        //alertEl.innerText = "Enter at least 4 characters"; //addressAlert;
        //errorEl.innerText = "Enter at least 4 characters"; //addressAlert;
      }
      input.classList.remove('error-class');
    } else {
      if (savedAddress.length > 0) {
        errorEl.style.display = 'block';
        errorEl.style.color = 'red';
        errorEl.style.padding = '5px';
        //alertEl.innerText = "Enter at least 4 charaters"; //addressAlert;
        errorEl.innerText = 'Enter at least 4 characters'; //addressAlert;
      }
    }

    if (getOfferBtn) {
      getOfferBtn.addEventListener('click', () =>
        handleGetOfferClick(input.value)
      );
    }

    return () => {
      input.removeEventListener('input', handleAddressInputChange);
    };
  }, [templateHtml, savedAddress, errorMessage]);

  const handleAddressChange = (event, val) => {
    handleStepClick(val);
  };

  useEffect(() => {
    if (addressSuggestions.length > 0 && containerRef.current) {
      const input = containerRef.current.querySelector(
        '#' + ELEMENT_IDS.ADDRESS_INPUT
      );
      if (input) {
        const rect = input.getBoundingClientRect();
        setDropdownStyle({
          position: 'fixed',
          top: `${rect.bottom}px`,
          left: `${rect.left}px`,
          width: `${rect.width}px`,
        });
      }
    }
  }, [addressSuggestions]);

  const debouncedFetchRef = useRef();
  const skipDebounceRef = useRef(false);

  useEffect(() => {
    debouncedFetchRef.current = debounce((val) => {
      fetchAddressAutocomplete(val);
    }, 300);

    return () => {
      debouncedFetchRef.current?.cancel?.();
    };
  }, []);

  const handleAddressInputChange = (e) => {
    const value = e.target.value;
    setSavedAddress(value);
    setAddressInputValue(value);
    document.querySelector('.error-message').style.display = 'none';
    //setIsAddressSet(false);
    const successalertBlock = containerRef.current?.querySelector(
      '.address-success-message'
    );
    if (successalertBlock) {
      successalertBlock.classList.remove('show');
      successalertBlock.style.display = 'none';
    }

    const comingSoonEl = containerRef.current?.querySelector(
      '.coming-soon-inline'
    );
    if (comingSoonEl) {
      comingSoonEl.style.display = 'none';
    }

    const formContainer = containerRef.current?.querySelector(
      '.checkForServiceNode-formContainer'
    );
    const bottomActions = containerRef.current?.querySelector(
      '.checkForServiceNode-bottomActions'
    );
    const signInText = containerRef.current?.querySelector(
      '.checkForServiceNode-signInText'
    );
    if (formContainer) formContainer.style.display = 'flex';
    if (bottomActions) bottomActions.style.display = 'flex';
    if (signInText) signInText.style.display = 'block';

    const formRows = containerRef.current?.querySelectorAll(
      '.checkForServiceNode-formRow'
    );
    if (formRows) {
      formRows.forEach((row) => {
        row.style.display = 'flex';
      });
    }

    greatNewsShownRef.current = false;

    if (value.length <= 3) {
      setIsAddressLoading(false);
      if (containerRef && containerRef.current) {
        const addressLoader =
          containerRef.current.querySelector('#address-loader');
        if (addressLoader) {
          addressLoader.style.display = 'none';
        }
      }
      setAddressSuggestions([]);
      setAddressId('');
      setServiceAddress(null);
      setValidationFlag(false);
      setErrorMessage('');
      setAddressAlert('');

      // Clear the coming-soon message when input is cleared or too short
      const comingSoonMessage =
        containerRef.current?.querySelector('.coming-soon');
      if (comingSoonMessage) {
        const errorEl = comingSoonMessage.parentElement;
        if (errorEl) {
          errorEl.innerHTML = '';
          errorEl.style.display = 'none';
        }
      }

      const cityInput = containerRef.current?.querySelector('#city');
      const stateInput = containerRef.current?.querySelector('#state');
      const zipInput = containerRef.current?.querySelector('#zip-code');
      if (cityInput) cityInput.value = '';
      if (stateInput) stateInput.value = '';
      if (zipInput) zipInput.value = '';

      // if (value.length > 0) {
      //   setSnackbarMessage(
      //     'Please enter at least 4 characters to search for addresses'
      //   );
      //   setSnackbarSeverity('info');
      //   setSnackbarOpen(true);
      // }
      return;
    }

    const filteredNode = workflow.nodes.find(
      (node) => node.id === currentStepId
    );

    if (filteredNode?.type === 'checkForServiceNode') {
      if (skipDebounceRef.current) {
        skipDebounceRef.current = false;
        return;
      }
      debouncedFetchRef.current(value);
    }
  };

  const fetchAddressAutocomplete = async (searching) => {
    setIsAddressSet(false);
    setValidationFlag(false);

    setIsAddressLoading(true);
    setAddressSuggestions([]);

    if (containerRef && containerRef.current) {
      const addressLoader =
        containerRef.current.querySelector('#address-loader');
      if (addressLoader) {
        addressLoader.style.display = 'block';
      }
    }

    if (containerRef && containerRef.current) {
      const preRegBtn = containerRef.current.querySelector(
        '.pre-register-button'
      );
      if (preRegBtn) {
        preRegBtn.setAttribute('disabled', 'true');
      }

      const addressInput = containerRef.current.querySelector(
        '#' + ELEMENT_IDS.ADDRESS_INPUT
      );
      if (addressInput) {
        addressInput.style.backgroundImage =
          "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24'%3E%3Cpath fill='%23999' d='M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z'%3E%3CanimateTransform attributeName='transform' dur='0.75s' repeatCount='indefinite' type='rotate' values='0 12 12;360 12 12'/%3E%3C/path%3E%3C/svg%3E\")";
        addressInput.style.backgroundRepeat = 'no-repeat';
        addressInput.style.backgroundPosition = 'right 12px center';
        addressInput.style.backgroundSize = '20px 20px';
      }
    }

    try {
      const query = searching.trim();
      let sa = '';
      let city = '';
      let state = '';
      let pc = '';
      if (!query) {
        setIsAddressLoading(false);

        if (containerRef && containerRef.current) {
          const addressLoader =
            containerRef.current.querySelector('#address-loader');
          if (addressLoader) {
            addressLoader.style.display = 'none';
          }
        }
        return;
      }

      const params = new URLSearchParams();

      if (/\d/.test(query)) {
        params.append('serviceAddress', query);
        sa = query;
      }

      if (/^\d{4,9}$/.test(query)) {
        params.append('postalCode', query);
        pc = query;
        sa = '';
      }

      if (/^[A-Za-z]{2}$/.test(query)) {
        params.append('stateAbbreviation', query.toUpperCase());
        state = query;
      }

      if (
        !params.has('serviceAddress') &&
        !params.has('postalCode') &&
        !params.has('stateAbbreviation')
      ) {
        params.append('cityName', query);
        city = query;
      }

      if (query.length > 0) {
        setErrorMessage('Searching for addresses...');
      }
      const payload = {
      serviceAddress: sa,
            cityName: city,
            stateAbbreviation: state,
            postalCode: pc,
    };
    const response = await axios.post(
      `${process.env.REACT_APP_BASE_URL}/workflows/search/services/`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `${token}`,
        },
      }
    );
      if (response.status === 200) {
        const subscribers = response.data.serviceAddresses || [];
        const suggestions = subscribers.flatMap((service) => ({
          addressLine1: service.addressLine1,
          addressCityName: service.cityName,
          addressStateAbbr: service.stateAbbreviation,
          addressPostalCode: service.postalCode,
          serviceAddressId: service.serviceAddressId,
        }));

        console.log(suggestions, 'suggestions');
        if (suggestions?.length > 0) {
          setIsAddressAvailable(true);
        } else {
          setIsAddressAvailable(false);
        }

        if (suggestions && suggestions.length > 0) {
          const comingSoonEl = containerRef.current?.querySelector(
            '.coming-soon-inline'
          );
          if (comingSoonEl) {
            comingSoonEl.style.display = 'none';
          }

          const formContainer = containerRef.current.querySelector(
            '.checkForServiceNode-formContainer'
          );
          const bottomActions = containerRef.current.querySelector(
            '.checkForServiceNode-bottomActions'
          );
          const signInText = containerRef.current.querySelector(
            '.checkForServiceNode-signInText'
          );

          if (formContainer) formContainer.style.display = 'flex';
          if (bottomActions) bottomActions.style.display = 'flex';
          if (signInText) signInText.style.display = 'block';

          setIsAddressAvailable(true);
          setAddressSuggestions(suggestions);

          setAddressAlert('');
          setErrorMessage('');

          if (containerRef && containerRef.current) {
            const preRegBtn = containerRef.current.querySelector(
              '.pre-register-button'
            );
            if (preRegBtn) {
              preRegBtn.removeAttribute('disabled');
            }

            const addressInput = containerRef.current.querySelector(
              '#' + ELEMENT_IDS.ADDRESS_INPUT
            );
            if (addressInput) {
              addressInput.style.backgroundImage = 'none';
            }
          }
        } else {
          const successalertBlock = containerRef.current?.querySelector(
            '.address-success-message'
          );
          if (successalertBlock) {
            successalertBlock.classList.remove('show');
            successalertBlock.style.display = 'none';
          }
          setIsAddressAvailable(false);
          setAddressAlert('No address available');
          setErrorMessage('No address available');
          setAddressSuggestions([]);

          const addressInput = containerRef.current?.querySelector(
            '#' + ELEMENT_IDS.ADDRESS_INPUT
          );
          const addressValue = addressInput?.value || savedAddress || '';

          if (addressValue.length >= 12) {
            const parentElement = containerRef.current?.querySelector(
              'input[class*="address"]'
            )?.parentNode;

            let comingSoonBlock = containerRef.current?.querySelector(
              '.coming-soon-inline'
            );

            if (!comingSoonBlock && parentElement) {
              comingSoonBlock = document.createElement('div');
              comingSoonBlock.className = 'coming-soon-inline';
              parentElement.appendChild(comingSoonBlock);
            }
          }

          if (containerRef && containerRef.current) {
            const addressInput = containerRef.current.querySelector(
              '#' + ELEMENT_IDS.ADDRESS_INPUT
            );
            if (addressInput) {
              addressInput.style.backgroundImage = 'none';
            }
          }
        }
      } else {
        console.error('Failed to fetch address autocomplete suggestions');
        setErrorMessage('Failed to search addresses');
      }
    } catch (error) {
      console.error('Error fetching address autocomplete suggestions:', error);
      setErrorMessage('Error searching addresses');
    } finally {
      setIsAddressLoading(false);

      if (containerRef && containerRef.current) {
        const addressLoader =
          containerRef.current.querySelector('#address-loader');
        if (addressLoader) {
          addressLoader.style.display = 'none';
        }
      }

      if (containerRef && containerRef.current) {
        const addressInput = containerRef.current.querySelector(
          '#' + ELEMENT_IDS.ADDRESS_INPUT
        );
        if (addressInput) {
          addressInput.style.backgroundImage = 'none';
        }
      }
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (!suggestion || typeof suggestion !== 'object') return;

    const addressString = `${suggestion.addressLine1}, ${suggestion.addressCityName}, ${suggestion.addressStateAbbr} ${suggestion.addressPostalCode?.slice(0, 5)}`;

    skipDebounceRef.current = true;

    setSavedAddress(addressString);
    setIsAddressSet(true);
    setAddressInputValue(addressString);
    setServiceAddress(suggestion);
    setAddressId(suggestion.serviceAddressId);

    setAddressSuggestions([]);

    const streetAddressInput = containerRef.current.querySelector(
      '#' + ELEMENT_IDS.ADDRESS_INPUT
    );
    const cityInput = containerRef.current.querySelector('#city');
    const stateInput = containerRef.current.querySelector('#state');
    const zipInput = containerRef.current.querySelector('#zip-code');

    // Check if it's a simple form (PRTC) or detailed form (Archtop)
    const isDetailedForm = COMPANY_CONFIG?.ui?.addressForm?.type === 'detailed';

    if (streetAddressInput) {
      // For simple form, show full address in the single input
      // For detailed form, show only street address
      streetAddressInput.value = isDetailedForm
        ? suggestion.addressLine1 || ''
        : addressString;
      streetAddressInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    // Only populate separate fields for detailed form
    if (isDetailedForm) {
      if (cityInput) {
        cityInput.value = suggestion.addressCityName || '';
      }
      if (stateInput) {
        stateInput.value = suggestion.addressStateAbbr || '';
      }
      if (zipInput) {
        zipInput.value = suggestion.addressPostalCode?.slice(0, 5) || '';
      }
    }

    // ==================== COMPANY-SPECIFIC: SHOW SUCCESS MESSAGE ====================
    // For PRTC/Interbel (single-click), show "Great News!" immediately when address is selected
    if (COMPANY_CONFIG.features.showGreatNewsOnFirstClick) {
      console.log(
        `🎉 Showing Great News for ${COMPANY_CONFIG.name} - address selected`
      );

      const parentElement = containerRef.current?.querySelector(
        'input[class*="address"]'
      )?.parentNode;

      let alertBlock = containerRef.current?.querySelector(
        '.address-success-message'
      );

      if (!alertBlock && parentElement) {
        alertBlock = document.createElement('div');
        alertBlock.className = 'address-success-message';
        parentElement.appendChild(alertBlock);
        console.log('✅ Created new alert block');
      }

      if (alertBlock) {
        console.log('✅ Showing Great News message', { isAddressAvailable });
        alertBlock.innerHTML = `
          <img src="/images/icons/successlikeicon.png" alt="Success" class="success-icon" />
          <span><strong>Great News!</strong> We can provide service at your address.</span>
        `;
        alertBlock.classList.add('show');
        alertBlock.style.display = 'block';
      } else {
        console.log('❌ Alert block not found');
      }
    } else {
      console.log(
        'ℹ️ Not showing Great News - showGreatNewsOnFirstClick is false'
      );
    }

    setValidationFlag(true);
    setAddressSuggestions([]);
  };

  const skipInvalidAddress = () => {
    const container = containerRef.current.querySelector(
      '.checkForServiceNode-Container'
    );
    const addressInput = document.getElementById('address-input');
    if (addressInput) {
      addressInput.value = '';
    }
    const parent = addressInput.parentNode;
    const alertEl =
      parent.querySelector('.address-alert') ||
      (() => {
        const el = document.createElement('div');
        el.className = 'address-alert';
        addressInput.insertAdjacentElement('afterend', el);
        return el;
      })();

    const successEl =
      parent.querySelector('.address-success-message') ||
      (() => {
        const el = document.createElement('div');
        el.className = 'address-success-message';
        parent.appendChild(el);
        return el;
      })();
    const errorEl =
      parent.querySelector('.error-message') ||
      (() => {
        const el = document.createElement('div');
        el.className = 'error-message';
        parent.appendChild(el);
        return el;
      })();
    errorEl.style.display = 'none';
    successEl.style.display = 'none';
  };

  const handleGetNotified = () => {
    const container = containerRef.current.querySelector(
      '.checkForServiceNode-Container'
    );

    container.innerHTML = renderToHTMLString(<GetNotified />, { minify: true });

    const moveToRegistrationBtn = container.querySelector(
      '#move-to-registration'
    );
    const firstName = container.querySelector('#firstName');
    const lastName = container.querySelector('#lastName');
    const emailAddress = container.querySelector('#emailAddress');

    const validateEmail = () => {
      const emailValue = emailAddress?.value.trim() || '';
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      if (!emailPattern.test(emailValue)) {
        emailAddress.classList.add('error-class');
        return false;
      } else {
        emailAddress.classList.remove('error-class');
        return true;
      }
    };

    if (emailAddress) {
      emailAddress.addEventListener('blur', validateEmail);
    }

    if (moveToRegistrationBtn) {
      moveToRegistrationBtn.addEventListener('click', () => {
        const firstNameVal = firstName?.value || '';
        const lastNameVal = lastName?.value || '';

        if (!validateEmail()) {
          emailAddress.focus();
          return;
        }

        saveAddressDetails(
          firstNameVal,
          lastNameVal,
          emailAddress.value.trim()
        );
      });
    }
  };

  const saveAddressDetails = async (fName, lName, email) => {
    setIsLoading(true);
    const container = containerRef.current.querySelector(
      '.checkForServiceNode-Container'
    );
    const payload = {
      firstName: fName,
      lastName: lName,
      emailAddress: email,
    };
    const response = await axios.post(
      `${process.env.REACT_APP_ACCOUNT_MANAGEMENT_URL}/subscriber/address-save`,
      payload,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `${token}`,
        },
      }
    );

    if (response.status === 200 && response.data.success) {
      container.innerHTML = renderToHTMLString(<RegistrationSuccess />, {
        minify: true,
      });
      setIsLoading(false);

      const backToHomeBtn = container.querySelector('#back-to-home');
      if (backToHomeBtn) {
        backToHomeBtn.addEventListener('click', () => {
          container.innerHTML = 'Loading...';
          setIsLoading(true);
          setAddressSuggestions([]);
          setTemplateHtml('');
          setReloadKey((prev) => prev + 1);

          setTimeout(() => {
            const addressInput = document.getElementById('address-input');
            if (addressInput) {
              addressInput.value = '';
              const event = new Event('input', { bubbles: true });
              addressInput.dispatchEvent(event);

              addressInput.removeEventListener(
                'input',
                handleAddressInputChange
              );
              addressInput.addEventListener('input', handleAddressInputChange);
              setIsLoading(false);
              console.log('✅ Cleared address input and triggered input event');
            } else {
              console.log('⚠️ address-input not found yet');
            }
          }, 1000);
          window.location.href = window.location.href;
        });
      }
    } else {
      setSnackbarMessage(response.data.message);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setIsLoading(false);
    }
  };

  const handleCheckoutProceedPress = () => {
    const container = document.getElementById('reviewCartNode-screen');
    container.innerHTML = renderToHTMLString(<TermsConditionNode />, {
      minify: true,
    });

    const checkbox = document.getElementById('termsConditionNode-checkbox');
    const button = document.getElementById(ELEMENT_IDS.NEXT_STEP_BUTTON);

    if (checkbox && button) {
      const updateButtonState = () => {
        const isChecked = checkbox.checked;
        button.disabled = !isChecked;
      };

      checkbox.addEventListener('change', updateButtonState);
      updateButtonState();

      button.addEventListener('click', () => {
        const currentIndex = orderedNodes.findIndex(
          (node) => node.id === currentStepId
        );

        if (currentIndex >= 0 && currentIndex < orderedNodes.length - 1) {
          const nextNode = orderedNodes[currentIndex + 1];
          setCurrentStepId(nextNode.id);
          setTemplateHtml('');
          setAddressSuggestions([]);
          setContinue(nextNode.type, orderedNodes.length - currentIndex - 2);
        }
      });
    }

    // Load the PDF into the iframe
    const loadTerms = (retryCount = 0, termsUrl = null) => {
      const iframe = document.querySelector('.termsConditionNode-iframe');
      if (iframe) {
        const companyName = COMPANY_CONFIG.name?.toLowerCase();
        console.log(
          '📄 Loading Terms PDF for:',
          companyName,
          'ThemeId:',
          COMPANY_CONFIG.themeId,
          'Retry:',
          retryCount
        );

        setIsLoading(true);

        // Setup iframe load handlers
        const handleIframeLoad = () => {
          console.log('✅ Terms loaded successfully');
          setIsLoading(false);
          iframe.removeEventListener('load', handleIframeLoad);
          iframe.removeEventListener('error', handleIframeError);
        };

        const handleIframeError = () => {
          console.error('❌ Failed to load terms iframe');

          if (retryCount < 2) {
            console.log(`🔄 Retrying... (Attempt ${retryCount + 2}/3)`);
            iframe.removeEventListener('load', handleIframeLoad);
            iframe.removeEventListener('error', handleIframeError);
            setTimeout(() => loadTerms(retryCount + 1, termsUrl), 1000);
          } else {
            setIsLoading(false);
            alert(
              'Failed to load Terms & Conditions after 3 attempts. Please try again or contact support.'
            );
            iframe.removeEventListener('load', handleIframeLoad);
            iframe.removeEventListener('error', handleIframeError);
          }
        };

        iframe.addEventListener('load', handleIframeLoad);
        iframe.addEventListener('error', handleIframeError);

        // If we already have the URL (from retry), use it
        if (termsUrl) {
          console.log('📄 Retrying with URL:', termsUrl);
          iframe.setAttribute('src', termsUrl);
          iframe.setAttribute('title', 'Terms and Conditions');
        }
        // Check if company has a direct terms URL (not from API)
        else if (COMPANY_CONFIG.termsUrl) {
          console.log('📄 Loading Terms directly from URL for:', companyName);
          iframe.setAttribute('src', COMPANY_CONFIG.termsUrl);
          iframe.setAttribute('title', 'Terms and Conditions');
        } else {
          console.log('📄 Loading terms from API for:', companyName);
          // For PRTC and Archtop, load from API (only once)
          fetchTermsFromAPI();
        }
      } else {
        console.warn('⚠️ Terms iframe not found, retrying...');
        setTimeout(() => loadTerms(retryCount, termsUrl), 200);
      }
    };

    const fetchTermsFromAPI = async () => {
      try {
        const api = `${process.env.REACT_APP_PERSONALIZE_URL}/customers/${getApiCustomerId()}/settings`;
        const data = await directApiRequest(api, 'GET', {}, token, {});
        const terms = data?.terms_and_conditions;
        const iframe = document.querySelector('.termsConditionNode-iframe');

        if (!terms) {
          throw new Error('No terms & conditions found in API response');
        }

        if (terms && iframe) {
          let termsUrl = null;

          if (terms.type === 'url' && terms.value) {
            termsUrl = terms.value;
          } else if (terms.type === 'document' && terms.document_name) {
            termsUrl = `${process.env.REACT_APP_PERSONALIZE_URL}/documents/${terms.document_name}`;
          } else {
            throw new Error('Invalid terms format');
          }

          // Set the URL and let iframe handlers manage retries
          iframe.setAttribute('src', termsUrl);
          iframe.setAttribute('title', 'Terms and Conditions');

          // Store URL for potential retries
          iframe.dataset.termsUrl = termsUrl;
        }
      } catch (error) {
        console.error('Failed to fetch terms from API:', error);
        setIsLoading(false);
        alert(
          'Failed to load Terms & Conditions from API. Please ensure they are configured in Element Workspace.'
        );
      }
    };

    loadTerms();
  };
  // ====================== END ADDRESS NODE ======================

  // LANDINGPAGE NODE
  useEffect(() => {
    if (containerRef.current && workflow && workflow.nodes) {
      const filteredNode = workflow.nodes.find(
        (node) => node.id === currentStepId
      );
      if ('landingPageNode' == filteredNode.type) {
        setValidationFlag(true);
      }
    }
  }, [templateHtml, savedAddress]);

  const [isIframeVisible, setIframeVisible] = useState(true);
  const [iframeHeight, setIframeHeight] = useState('100%');

  const iframeErrorRef = useRef(false);
  const isFirstLoadRef = useRef(true);

  const handleMessage = (event) => {
    if (event.origin !== 'https://secure1.paymentus.com') return;

    // Only process payment messages if payment is available
    if (!PAYMENT_AVAILABLE) return;

    const data = event.data;

    if (data.startsWith('Result:')) {
      try {
        const jsonString = data.replace('Result:', '').trim();
        const parsedData = JSON.parse(jsonString);

        if (parsedData['ERROR:'] && parsedData['ERROR:'].length > 0) {
          iframeErrorRef.current = true;
          isFirstLoadRef.current = true;
          return;
        }

        iframeErrorRef.current = false;

        const modal = document.getElementById('payment-modal');
        modal?.classList.add('hidden');
      } catch (err) {
        console.error('Failed to parse JSON:', err);
      }
    } else {
    }

    if (data.startsWith('frameHeight:')) {
      const newHeight = data.split(':')[1]?.trim();
      if (newHeight) {
        setIframeHeight(`${newHeight}px`);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const handleIframeLoad = () => {
    if (isFirstLoadRef.current) {
      setIframeVisible(true);
      isFirstLoadRef.current = false;
    } else {
      setIframeVisible(false);
      const modal = document.getElementById('payment-modal');
      if (!iframeErrorRef.current) {
        modal?.classList.add('hidden');
      } else {
        console.log('Iframe reported an error, keeping modal open.');
      }
    }
  };

  // ======================= START PAYMENT NODE =========================
  useEffect(() => {
    if (!containerRef.current || !workflow?.nodes) return;

    const node = workflow.nodes.find((n) => n.id === currentStepId);
    if (node?.type !== 'paymentNode') return;

    const container = containerRef.current;
    const headerElement = container.querySelector(
      '.paymentNode-header-container h2'
    );

    if (headerElement) {
      if (autoPayValue === 'yes') {
        headerElement.textContent = 'Select AutoPay Method';
      } else {
        headerElement.textContent = 'Select Payment Method';
      }
    }
    const titleElement = containerRef.current.querySelector(
      '.paymentNode-cardTitle'
    );

    if (titleElement) {
      if (autoPayValue == 'yes') {
        titleElement.textContent = 'Select AutoPay Method';
      } else {
        titleElement.textContent = 'Select Payment Method';
      }
    }
    const options = container.querySelectorAll('.paymentNode-option');
    const modal = document.getElementById('payment-modal');
    const modalContent = modal?.querySelector(
      '.paymentNode-modalIframeWrapper'
    );
    const skipButton = document.getElementById(ELEMENT_IDS.SKIP_BUTTON);
    const closeBtn = modal?.querySelector('.paymentNode-modalClose');

    // Hide skip button for Highline (payment is mandatory)
    if (skipButton && !canSkipPayment) {
      skipButton.style.display = 'none';
    }

    const closeModal = () => {
      // Clear iframe before closing modal
      const iframe = modalContent?.querySelector('iframe');
      if (iframe) {
        iframe.src = 'about:blank';
        iframe.remove();
      }
      modal?.classList.add('hidden');
      modalContent?.replaceChildren();
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeModal();
    };

    const handleOptionClick = async (option) => {
      options.forEach((opt) =>
        opt.classList.remove('paymentNode-option--selected')
      );
      option.classList.add('paymentNode-option--selected');

      const labelText =
        option.querySelector('span')?.innerText?.toLowerCase() || '';

      const paymentType = labelText.includes('credit') ? 'CC' : 'DD';
      if (!paymentType || !modal || !modalContent) return;
      isFirstLoadRef.current = true;

      const timestamp = Date.now().toString();

      const externalReference = `WF_${timestamp}_${autoPayValue === 'yes' ? 1 : 0}`;

      setexternalReference(externalReference);
      isFirstLoadRef.current = true;

      try {
        modalContent.innerHTML = `
  <div style="
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  ">
    <div style="
      width: 60px;
      height: 60px;
      border: 6px solid #ccc;
      border-top: 6px solid #333;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    "></div>
  </div>
`;

        modal.classList.remove('hidden');

        const iframeHTML = await generatePaymentIframeHTML({
          paymentType,
          subscriberId,
          guarantorId,
          personId,
          emailId,
          firstname,
          lastname,
          amount: selectedProduct.price || 0,
          externalReference,
          timestamp,
          accessToken: token,
        });

        modalContent.innerHTML = iframeHTML;

        const iframe = modalContent.querySelector('iframe');
        if (iframe) {
          iframe.addEventListener('load', handleIframeLoad);
        } else {
        }
      } catch (err) {
        console.error('Failed to generate payment iframe:', err);

        // Check if payment is not available for this company
        if (!PAYMENT_AVAILABLE) {
          modalContent.innerHTML = `<div class="payment-error">Payment is not available for this company at the moment.</div>`;
        } else {
          modalContent.innerHTML = `<div class="payment-error">Failed to load payment. Please try again.</div>`;
        }
      }
    };

    options.forEach((option) => {
      option.addEventListener('click', () => handleOptionClick(option));
    });

    closeBtn?.addEventListener('click', closeModal);
    document.addEventListener('keydown', handleKeyDown);
    skipButton?.addEventListener('click', () => {
      // Only allow skip if not Highline
      if (canSkipPayment) {
        setSkipPayment(true);
        handleNext(true);
      }
    });

    // Validation for next button - prevent proceeding without payment selection
    const nextButton = container.querySelector(
      `#${ELEMENT_IDS.NEXT_STEP_BUTTON}`
    );

    const validatePaymentSelection = (e) => {
      const selectedOption = container.querySelector(
        '.paymentNode-option--selected'
      );

      if (!selectedOption && !canSkipPayment) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();

        setSnackbarMessage(
          'Please select a payment method (eCheck or Credit Card) to continue'
        );
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        console.log('❌ Payment validation failed - no method selected');
        return false;
      }

      // For Highline, validate that payment details were actually entered
      /*if (selectedOption && !canSkipPayment) {
        const hasCardDetails =
          autoPayValue === 'yes'
            ? (newPaymentDetails.eftCreditCardBrand &&
                newPaymentDetails.eftCreditCardNumber) ||
              newPaymentDetails.eftBankAccountNumber
            : (newPaymentDetails.creditCardType &&
                newPaymentDetails.creditCardNumberLast4) ||
              newPaymentDetails.bankAccountLast4;

        if (!hasCardDetails) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          setSnackbarMessage('Please enter your payment details to continue');
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
          console.log(
            '❌ Payment validation failed - no payment details entered'
          );
          return false;
        }
      }*/

      if (
        !selectedOption &&
        canSkipPayment &&
        !skipPaymentConfirmedRef.current
      ) {
        setPendingSkipPaymentEvent(e);
        setSkipPaymentDialogOpen(true);
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        return false;
      }

      console.log('✅ Payment validation passed');
      return true;
    };

    if (nextButton) {
      nextButton.addEventListener('click', validatePaymentSelection, true);
    }

    return () => {
      options.forEach((option) => {
        option.replaceWith(option.cloneNode(true));
      });
      closeBtn?.removeEventListener('click', closeModal);
      document.removeEventListener('keydown', handleKeyDown);
      // Cleanup next button listener
      if (nextButton) {
        nextButton.removeEventListener('click', validatePaymentSelection, true);
      }
    };
  }, [templateHtml, autoPayValue]);
  // ======================= END PAYMENT NODE =========================

  // ======================= START SELF SCHEDULING NODE =========================
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !workflow?.nodes) return;

    const filteredNode = workflow.nodes.find(
      (node) => node.id === currentStepId
    );

    if (filteredNode?.type !== 'selfSchedulingNode') return;

    container.innerHTML = '';
    const rootElement = document.createElement('div');
    container.appendChild(rootElement);

    const root = createRoot(rootElement);
    root.render(
      <SelfSchedulingNode
        htmlContent={templateHtml}
        token={token}
        onApplyChanges={(data) => {
          console.log('📅 Self-scheduling data received:', data);
          // Pass the entire data object including formatted display date
          if (data) {
            setScheduledInstallationDate(data);
          }
        }}
      />
    );

    return () => {
      setTimeout(() => {
        root.unmount();
      }, 0);
    };
  }, [templateHtml]);

  // progressbar
  const getOrderedNodes = (nodes, edges) => {
    if (!nodes.length) return [];

    const orderedNodes = [];
    const startingNode = nodes.find(
      (node) => !edges.some((edge) => edge.target === node.id)
    );
    if (!startingNode) return orderedNodes;

    let currentNode = startingNode;
    orderedNodes.push(currentNode);

    const visitedNodes = new Set();
    visitedNodes.add(currentNode.id);
    const isContinue = setContinue(currentNode?.type);
    setIsContinueVisible(isContinue);

    while (currentNode) {
      const nextNode = getNextNode(currentNode, nodes, edges, visitedNodes);
      if (nextNode) {
        orderedNodes.push(nextNode);
        visitedNodes.add(nextNode.id);
        currentNode = nextNode;
      } else {
        currentNode = null;
      }
    }

    return orderedNodes;
  };

  const getNextNode = (currentNode, nodes, edges, visitedNodes) => {
    const outgoingEdge = edges.find(
      (edge) => edge.source === currentNode.id && !visitedNodes.has(edge.target)
    );
    if (outgoingEdge) {
      return nodes.find((node) => node.id === outgoingEdge.target);
    }
    return null;
  };

  const getNoContentMessage = () => `
    <div class='full-page-message' style="display: flex;align-items: center;justify-content: center;height: 100vh;width: 100%;background-color: #f4f4f9;color: #333;font-family: "Arial", sans-serif;">
        <div class='message-container' style="text-align: center;padding: 20px;border-radius: 8px;box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
            <h1 style="margin-bottom: 20px;color: #4a90e2;">Notice</h1>
            <p style="font-size: 1.2rem;line-height: 1.5;">No content is available at the moment. Please check back later.</p>
        </div>
    </div>
  `;

  const currentNode = orderedNodes.find((node) => node.id === currentStepId);

  const handleNext = (val) => {
    const currentIndex = orderedNodes.findIndex(
      (node) => node.id === currentStepId
    );
    if (
      currentIndex < orderedNodes.length - 1 &&
      (false !== validationFlag || true === val)
    ) {
      setCurrentStepId(orderedNodes[currentIndex + 1].id);
      const isContinue = setContinue(
        orderedNodes[currentIndex + 1].type,
        orderedNodes.length - (currentIndex + 1)
      );
      setIsContinueVisible(isContinue);
      setTemplateHtml('');
      setAddressSuggestions([]);
    }
  };

  const handleStepClick = (nodeId) => {
    const isAccessible =
      orderedNodes.findIndex((node) => node.id === nodeId) <=
      orderedNodes.findIndex((node) => node.id === currentStepId);
    if (isAccessible && nodeId != currentStepId) {
      setCurrentStepId(nodeId);
      setTemplateHtml('');
      setAddressSuggestions([]);
      const currentIndex = orderedNodes.findIndex((node) => node.id === nodeId);
      setContinue(
        orderedNodes[currentIndex].type,
        orderedNodes.length - (currentIndex + 1)
      );
    }
  };

  const closeModal = () => {
    setModalStatus(false);
  };

  const setContinue = (nodeType, index) => {
    if (index === 1) {
      return false;
    }
    if (ancestorOrigins && ancestorOrigins.length === 0) {
      return true;
    } else {
      const allowedNodeTypes = [
        'landingPageNode',
        'serviceSelectionNode',
        'addOnsNode',
      ];
      return allowedNodeTypes.includes(nodeType);
    }
  };

  const isInIframe = window.self !== window.top;

  const wrapperStyle = {
    position: 'relative',
  };

  const fixedHeaderStyle = {
    padding: '50px 0px',
    top: 0,
  };

  const headerContentStyle = {
    maxWidth: 1200,
    padding: '0 20px',
    margin: '0 auto',
    width: '100%',
  };

  const closeButtonStyle = {
    border: '1px solid #000',
    padding: 8,
    borderRadius: 20,
    cursor: 'pointer',
    position: 'absolute',
    right: 15,
    top: 15,
    zIndex: 1001,
  };

  const loaderOverlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 9999,
  };

  const loaderSpinnerStyle = {
    width: 60,
    height: 60,
    border: '6px solid #ccc',
    borderTop: '6px solid #333',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  };

  const suggestionDropdownStyle = {
    position: 'absolute',
    left: 0,
    top: '100%', // Position directly below the input
    width: '100%',
    maxHeight: 180,
    overflowY: 'auto',
    border: '1px solid #ccc',
    borderRadius: 4,
    backgroundColor: '#fff',
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
    zIndex: 1000,
    marginTop: 2, // Small 2px gap
  };

  const suggestionItemStyle = {
    padding: 10,
    cursor: 'pointer',
    borderBottom: '1px solid #eee',
  };

  return (
    <div style={wrapperStyle} className="multiform">
      <Header />
      <div style={fixedHeaderStyle} className="multiform__progress">
        <div style={headerContentStyle}>
          <ProgressTracker
            currentStepId={currentStepId}
            nodes={orderedNodes}
            edges={workflow?.edges || ''}
            onStepClick={handleStepClick}
          />

          {/* Company Indicator & Test Switcher */}
          <div
            style={{
              position: 'absolute',
              top: '10px',
              right: '60px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              alignItems: 'flex-end',
            }}
          >
            <div
              style={{
                background:
                  COMPANY_CONFIG.name === 'ARCHTOP' ? '#4CAF50' : '#2196F3',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '4px',
                fontSize: '12px',
                fontWeight: 'bold',
                textAlign: 'center',
                minWidth: '120px',
                display: 'none',
              }}
            >
              <div style={{ fontSize: '10px', opacity: 0.9 }}>
                {COMPANY_CONFIG.features.requireDoubleClickForAddress
                  ? 'Double-Click Mode'
                  : 'Single-Click Mode'}
              </div>
              {COMPANY_CONFIG.name === 'ARCHTOP' && (
                <div
                  style={{ fontSize: '10px', opacity: 0.8, marginTop: '2px' }}
                >
                  SVG Icons | Detailed Form
                </div>
              )}
              {COMPANY_CONFIG.name === 'ARCHTOP' &&
                doubleClickStatus !== 'waiting' && (
                  <div
                    style={{
                      fontSize: '9px',
                      marginTop: '2px',
                      padding: '1px 3px',
                      background: 'rgba(255,255,255,0.2)',
                      borderRadius: '2px',
                    }}
                  >
                    {doubleClickStatus === 'first-click' && '1st Click ✓'}
                    {doubleClickStatus === 'ready-for-second' &&
                      'Ready for 2nd →'}
                    {doubleClickStatus === 'proceeding' && 'Proceeding...'}
                  </div>
                )}
              {(COMPANY_CONFIG.name === 'PRTC' ||
                COMPANY_CONFIG.name === 'Interbel') && (
                <div
                  style={{ fontSize: '10px', opacity: 0.8, marginTop: '2px' }}
                >
                  PNG Icons | Simple Form
                </div>
              )}
            </div>
          </div>

          {!isInIframe && (
            <button
              onClick={() =>
                navigate(`/workflow/${workflowId}`, { state: { workflowId } })
              }
              style={closeButtonStyle}
              aria-label="Close"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {isLoading && (
        <div style={loaderOverlayStyle}>
          <div style={loaderSpinnerStyle}></div>
        </div>
      )}

      {workflow &&
        currentStepId &&
        (() => {
          const currentNode = workflow.nodes.find(
            (node) => node.id === currentStepId
          );

          // Render AddOnsNode as React component if it's the alternate version
          if (
            currentNode?.type === 'addOnsNode' &&
            COMPANY_CONFIG.features?.customNodeHandling
          ) {
            return (
              <AddOnsNode
                htmlContent={templateHtml}
                setHtmlContent={setTemplateHtml}
                token={token}
                onApplyChanges={(data) => {
                  console.log('🎯 MultiStepForm - Received AddOns data:', data);
                  console.log(
                    '🎯 MultiStepForm - Current selectedAddOnsData before update:',
                    selectedAddOnsData
                  );
                  // Store the complete add-on data
                  setSelectedAddOnsData(data);
                  console.log(
                    '🎯 MultiStepForm - setSelectedAddOnsData called with:',
                    data
                  );
                  // Update selectedAddOns state based on the data
                  const newSelectedAddOns = {};
                  data.selectedAddOns.forEach((addon) => {
                    newSelectedAddOns[addon.id] = true;
                  });
                  setSelectedAddOns(newSelectedAddOns);
                  console.log(
                    '🎯 MultiStepForm - Updated selectedAddOns:',
                    newSelectedAddOns
                  );
                }}
              />
            );
          }

          // Render ReviewCartNode as React component if it's the alternate version
          if (
            currentNode?.type === 'reviewCartNode' &&
            COMPANY_CONFIG.features?.customNodeHandling
          ) {
            console.log('🔵 Rendering ReviewCartNode with data:', {
              selectedAddOnsData,
              scheduledInstallationDate,
              selectedProduct,
            });
            return (
              <ReviewCartNode
                htmlContent={templateHtml}
                token={token}
                selectedProduct={selectedProduct}
                selectedAddOnsData={selectedAddOnsData}
                onAutoPayChange={(enabled) => {
                  console.log('💳 AutoPay changed:', enabled);
                  setAutoPayValue(enabled ? 'yes' : 'no');
                }}
                onPaperlessChange={(enabled) => {
                  console.log('📄 Paperless changed:', enabled);
                  setPaperlessValue(enabled ? 'yes' : 'no');
                }}
                serviceAddress={serviceAddress}
                installationDate={scheduledInstallationDate}
                onEditInstallation={() => {
                  console.log('Edit installation clicked');
                  // Navigate back to scheduling step if needed
                }}
                onProceedToTerms={() => {
                  console.log(
                    '🔘 Proceed to terms clicked from ReviewCartAlternate'
                  );
                  handleCheckoutProceedPress();
                }}
              />
            );
          }

          // Render OrderReceiptNode as React component if it's the alternate version
          if (
            currentNode?.type === 'orderReceiptNode' &&
            COMPANY_CONFIG.features?.customNodeHandling
          ) {
            console.log('📄 Rendering OrderReceiptNode with data:', {
              selectedAddOnsData,
              selectedProduct,
              autoPayValue,
              paperlessValue,
              discount: calculateDiscount(),
            });
            return (
              <OrderReceiptNode
                selectedProduct={selectedProduct}
                selectedAddOnsData={selectedAddOnsData}
                serviceAddress={serviceAddress}
                userDetails={{
                  name:
                    signUpPayload?.ecareFirstName +
                    ' ' +
                    signUpPayload?.ecareLastName,
                  phone: mobileNumber, // Use mobileNumber state instead
                  email: signUpPayload?.ecareEmailAddress,
                }}
                autoPayEnabled={autoPayValue === 'yes'}
                paperlessEnabled={paperlessValue === 'yes'}
                paymentMethod={newPaymentDetails?.displayText}
                discount={calculateDiscount()}
              />
            );
          }

          // Otherwise render template HTML with containerRef
          return (
            <div
              ref={containerRef}
              dangerouslySetInnerHTML={{ __html: templateHtml }}
            />
          );
        })()}

      {addressSuggestions.length > 0 &&
        ReactDOM.createPortal(
          <div style={suggestionDropdownStyle}>
            {addressSuggestions.map((suggestion, index) => (
              <div
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                style={suggestionItemStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#f5f5f5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#fff';
                }}
              >
                {`${suggestion.addressLine1}, ${suggestion.addressCityName}, ${suggestion.addressStateAbbr} ${suggestion.addressPostalCode?.slice(0, 5)}`}
              </div>
            ))}
          </div>,
          document.getElementById('address-service')
        )}
      <ModalBox
        title={modalTitle}
        status={modalStatus}
        closeModal={closeModal}
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={autoHideDuration}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Dialog
        open={skipPaymentDialogOpen}
        onClose={handleSkipPaymentCancel}
        aria-labelledby="skip-payment-dialog-title"
        aria-describedby="skip-payment-dialog-description"
        PaperProps={{
          style: {
            minWidth: '400px',
            padding: '8px',
          },
        }}
      >
        <DialogTitle
          id="skip-payment-dialog-title"
          style={{
            fontSize: '1.5rem',
            fontWeight: 600,
            padding: '20px 24px 16px',
          }}
        >
          Skip Payment?
        </DialogTitle>
        <DialogContent style={{ padding: '8px 24px 20px' }}>
          <DialogContentText
            id="skip-payment-dialog-description"
            style={{
              fontSize: '1.4rem',
              color: '#666',
              lineHeight: '1.5',
            }}
          >
            No payment method selected. Do you want to skip payment?
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px' }}>
          <Button
            onClick={handleSkipPaymentCancel}
            color="primary"
            style={{
              fontSize: '1.4rem',
              padding: '8px 20px',
              textTransform: 'none',
            }}
          >
            No
          </Button>
          <Button
            onClick={handleSkipPaymentConfirm}
            color="primary"
            autoFocus
            style={{
              fontSize: '1.4rem',
              padding: '8px 20px',
              textTransform: 'none',
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MultiStepForm;
